import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { retentionCeilingAmount } from "../lib/retentionCeiling";
import { useParams, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useBreadcrumbContext } from "../hooks/useBreadcrumbContext";
import { useAuth } from "../hooks/useAuth";
import {
  ArrowLeft,
  X,
  Save,
  DollarSign,
  TrendingUp,
  FileText,
  AlertTriangle,
  Download,
  Upload,
  Edit2,
  Calendar,
  LayoutGrid,
  RefreshCw,
  CheckCircle2,
  Info,
  Search,
  Loader2,
  CircleDashed,
  XCircle,
  Percent,
  ExternalLink,
  MessageSquare,
  PenLine,
  PieChart,
  Shield,
  Hash,
  ArrowUpDown,
  Wrench,
  Clock,
  Filter,
} from "lucide-react";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { EntityCommentsModal } from "../components/EntityCommentsModal";
import { EntityCommentsBadge } from "../components/EntityCommentsBadge";
import { HireCommentsProvider } from "../components/HireComments";
import { applyHireDiscount } from "../lib/hireBillingPeriod";
import { entityCommentsDb } from "../lib/entityCommentsDb";
import { setSeqReportModuleOverride } from "../lib/seqReport";
import {
  computeUnreadCounts,
  normaliseFlag,
  toCommentMeta,
  toViewMeta,
  ENTITY_COMMENTS_TABLE,
  ENTITY_VIEWS_TABLE,
  type CommentEntityType,
  type CommentFlag,
} from "../lib/entityComments";
import {
  formatCurrency,
  calculateContractValue,
  MONTH_NAMES,
} from "../lib/calculations";
import { logActivity } from "../lib/activity-logger";
import {
  type GroupedVariation,
  buildVariationGroups,
  findSourceVariationGroup,
  hasNoDayLabour,
  isHireOnlyVariation,
} from "../lib/variationGroups";
import {
  type ReconciliationStatus,
  RECONCILIATION_OPTIONS,
  getReconciliationOption,
} from "../lib/reconciliationStatus";
import {
  downloadProgressCSVTemplate,
  parseProgressCSV,
  validateProgressCSV,
  type ValidationResult,
} from "../lib/csvProgressImport";
import { Modal, useToast } from "../components/ui/ToastModal";
import { EditCategoriesModal } from "../components/EditCategoriesModal";
import { ProgressClaimReportsTab } from "../components/ProgressClaimReportsTab";
import { EditProjectModal } from "../components/EditProjectModal";
import { ProgressRangeView } from "../components/ProgressRangeView";
import { ProjectSummarySection } from "../components/ProjectSummarySection";
import { exportFormAsPdf } from "../lib/pdfExport";
import { ProgressClaimModal } from "../components/ProgressClaimModal";
import { ProjectStatusTab } from "../components/ProjectStatusTab";
import { useSafeMode } from "../contexts/SafeModeContext";
import { ProjectNotesTab } from "../components/ProjectNotesTab";
import { ProjectVaultTab } from "../components/ProjectVaultTab";
import { HireRegisterSection } from "../components/HireRegisterSection";
import { CreateInDashpivotLink } from "../components/CreateInDashpivotLink";
import { useSubscription } from "../hooks/useSubscription";
import { useTourManager } from "../hooks/useTourManager";
import { TourEngine } from "../components/onboarding/TourEngine";
import { classifySyncLock } from "../lib/syncLock";
import { htmlToPlainText } from "../lib/htmlToPlainText";
import { getOperationalRetentionAmount } from "../lib/reconciliationSummary";
import {
  excludeRemovedFromDashpivot,
  extractRemovalNotices,
  filterNewNotices,
  isNotRemovedFromDashpivot,
} from "../lib/dashpivotRemoval";
import type {
  ClaimsProject,
  ClaimsCategory,
  ClaimsSubcategory,
  ClaimsProgress,
  ClaimsVariation,
} from "../types/database.types";
import { logError, logWarn } from "../lib/logError";

// Stub declarations for removed CSV variation import helpers (dead code still references these)
interface CsvValidationResult {
  valid: Array<Record<string, string>>;
  errors: Array<{ row: number; field: string; message: string }>;
  warnings: string[];
  summary: Record<string, unknown>;
}
 
const validateVariationsCSV: (() => CsvValidationResult) = () => ({
  valid: [],
  errors: [],
  warnings: [],
  summary: {},
});
 
const parseVariationsCSV: (() => string[]) = () => [];

type TabType = "overview" | "status" | "claims" | "variations" | "reports" | "notes" | "vault";

interface ProjectWithDetails extends ClaimsProject {
  categories: (ClaimsCategory & { subcategories: ClaimsSubcategory[] })[];
}

// Smart percentage formatting - shows whole number when exact, 1 decimal otherwise
function formatPercentageClean(value: number): string {
  const rounded = Math.round(value * 10) / 10; // Round to 1 decimal
  if (Number.isInteger(rounded)) {
    return `${rounded}%`;
  }
  return `${rounded.toFixed(1)}%`;
}

// Percentage display with tooltip showing exact value
function PercentageDisplay({
  value,
  prefix = "",
}: {
  value: number;
  prefix?: string;
}) {
  const exactValue = value.toFixed(4);
  const displayValue = formatPercentageClean(value);

  return (
    <span title={`Exact: ${prefix}${exactValue}%`} style={{ cursor: "help" }}>
      {prefix}
      {displayValue}
    </span>
  );
}

export function ProjectDetailPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const openVariationId = searchParams.get("openVariation");
  const openVariationDetailId = searchParams.get("openVariationDetail");
  // SEQ-169: notification deep link for an audit note on a hire register item
  const openHireItemId = searchParams.get("openHireItem");
  const openSubcategoryId = searchParams.get("openSubcategory");
  const claimsViewParam = searchParams.get("claimsView");
  const highlightCategoryId = searchParams.get("highlightCategory");
  const { profile } = useAuth();
  // SEQ-169 follow up. Same expression as RoleProtectedWrapper and the P&L tab
  // below, so the app has one definition of financial access rather than two.
  const hasFinancialAccess = Boolean(profile?.can_view_financials);
  const { isSafeMode } = useSafeMode();
  const { setLabel, removeLabel } = useBreadcrumbContext();

  const [project, setProject] = useState<ProjectWithDetails | null>(null);
  const [progress, setProgress] = useState<ClaimsProgress[]>([]);
  const [variations, setVariations] = useState<ClaimsVariation[]>([]);
  const [discounts, setDiscounts] = useState<
    { description: string; amount: number }[]
  >([]);
  const [retentionTotal, setRetentionTotal] = useState<number>(0);
  const [retentionReceived, setRetentionReceived] = useState<number>(0);
  const [unreadVariationCounts, setUnreadVariationCounts] = useState<Record<string, number>>({});
  // SEQ-169: hire items carry the same audit notes as variations
  const [unreadHireCounts, setUnreadHireCounts] = useState<Record<string, number>>({});
  const [hireAuditFlags, setHireAuditFlags] = useState<Record<string, CommentFlag>>({});
  const [hireItemLabels, setHireItemLabels] = useState<Record<string, string>>({});
  const [unreadNotesCount, setUnreadNotesCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  // Support ?tab= query param for deep-linking from dashboard charts
  const VALID_TABS: TabType[] = ["overview", "status", "claims", "variations", "reports", "notes"];
  const tabFromUrl = searchParams.get("tab") as TabType | null;
  const initialTab: TabType = tabFromUrl && VALID_TABS.includes(tabFromUrl) ? tabFromUrl : "overview";

  const [activeTab, setActiveTab] = useState<TabType>(initialTab);
  const [reportsRefreshKey, setReportsRefreshKey] = useState(0);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showEditCategoriesModal, setShowEditCategoriesModal] = useState(false);
  const [isEditProjectOpen, setIsEditProjectOpen] = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const { addToast } = useToast();
  const { canCreateClaim, isSuspended } = useSubscription();
  const autoSyncRan = useRef(false);
  const [syncStatus, setSyncStatus] = useState<'idle' | 'syncing' | 'error'>('idle');
  const [, setSyncStartedAt] = useState<string | null>(null);
  const [syncElapsed, setSyncElapsed] = useState(0);
  const syncPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncPollErrorCountRef = useRef(0);

  // Deep link from the website admin: /projects/:id?edit=1 opens edit directly.
  const editParamHandled = useRef(false);
  useEffect(() => {
    if (project && !editParamHandled.current && searchParams.get("edit") === "1") {
      editParamHandled.current = true;
      setIsEditProjectOpen(true);
      searchParams.delete("edit");
      setSearchParams(searchParams, { replace: true });
    }
  }, [project, searchParams, setSearchParams]);

  // Auto-switch to variations tab if a variation or hire deep link is present
  useEffect(() => {
    if (openVariationId || openVariationDetailId || openHireItemId) {
      setActiveTab("variations");
    }
  }, [openVariationId, openVariationDetailId, openHireItemId]);

  // Auto-switch to claims tab if openSubcategoryId or highlightCategoryId is present (from notes mention deep link)
  useEffect(() => {
    if (openSubcategoryId || highlightCategoryId || claimsViewParam) {
      setActiveTab("claims");
      // Note: openSubcategory / highlightCategory params are cleaned up by ClaimsTab's onDeepLinkProcessed callback
    }
     
  }, [openSubcategoryId, highlightCategoryId, claimsViewParam]);

  // Auto-switch tab if ?tab= query param changes (e.g. from dashboard navigation)
  useEffect(() => {
    const tabParam = searchParams.get("tab") as TabType | null;
    if (tabParam && VALID_TABS.includes(tabParam) && tabParam !== activeTab) {
      setActiveTab(tabParam);
      // Clean up the query param after applying it
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("tab");
      setSearchParams(newParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // SEQ-192: the Status (P&L) tab is local state, not a route, so tell the
  // report widget when it is showing. P&L reports route to SEQ-180.
  useEffect(() => {
    setSeqReportModuleOverride(activeTab === "status" ? "pnl" : null);
    return () => setSeqReportModuleOverride(null);
  }, [activeTab]);

  // Fetch project data
  const fetchProject = useCallback(async (silent = false) => {
    if (!projectId) {
      console.error("[ProjectDetail] No projectId provided");
      return;
    }

    console.log(`[ProjectDetail] Fetching project (silent: ${silent})`);
    if (!silent) setLoading(true);
    try {
      // Fetch project
      const { data: projectData, error: projectError } = await supabase
        .from("claims_projects")
        .select("*")
        .eq("id", projectId)
        .single();

      
      if (projectError) {
        logError("[ProjectDetail] Project error:", projectError);
        throw projectError;
      }

      if (!projectData) {
        console.error("[ProjectDetail] No project data returned");
        return;
      }

      // Fetch discounts
      const { data: discountsData, error: discountsError } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
        .from("claims_discounts" as any)
        .select("*")
        .eq("project_id", projectId);

      if (discountsError) {
        logError("[ProjectDetail] Error fetching discounts:", discountsError);
        // Don't fail the whole page load for this
      }

      // Fetch categories with subcategories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from("claims_categories")
        .select(
          `
          *,
          subcategories:claims_subcategories(*)
        `,
        )
        .eq("project_id", projectId)
        .order("sort_order");

      
      if (categoriesError) throw categoriesError;

      // Data repair: Ensure fixed amount categories have FIXED AMOUNT subcategory
      const repairedCategories = categoriesData || [];

      // Sort subcategories by sort_order (Supabase fetch doesn't sort joined array automatically)
      repairedCategories.forEach((cat) => {
        if (cat.subcategories) {
          cat.subcategories.sort(
            (a: ClaimsSubcategory, b: ClaimsSubcategory) =>
              (a.sort_order ?? 0) - (b.sort_order ?? 0),
          );
        }
      });

      for (const cat of repairedCategories) {
        if (cat.is_custom_amount_only) {
          const hasFixedSub = (cat.subcategories || []).some(
            (s: ClaimsSubcategory) => s.name.toUpperCase() === "FIXED AMOUNT",
          );
          if (!hasFixedSub) {
            const { data: newSub, error: subError } = await supabase
              .from("claims_subcategories")
              .insert({
                organization_id: projectData.organization_id,
                name: "FIXED AMOUNT",
                amount: Number(cat.custom_amount) || 0,
                category_id: cat.id,
                sort_order: 0,
                is_default: false,
              })
              .select()
              .single();

            if (!subError && newSub) {
              cat.subcategories = [newSub, ...(cat.subcategories || [])];
            }
          }
        }
      }

      // Fetch progress claims in batches with detailed logging
      const subcategoryIds = (repairedCategories || []).flatMap((c) =>
        (c.subcategories || []).map((s: ClaimsSubcategory) => s.id),
      );

      let progressData: ClaimsProgress[] = [];
      if (subcategoryIds.length > 0) {
        console.group("[ProjectDetail] Fetching Claims Progress");
        console.log(`Total subcategories to fetch: ${subcategoryIds.length}`);
        
        // Use small batch size to isolate issues
        const chunkSize = 10;
        const batches = [];
        for (let i = 0; i < subcategoryIds.length; i += chunkSize) {
          batches.push(subcategoryIds.slice(i, i + chunkSize));
        }

        console.log(
          `Split into ${batches.length} batches of size ${chunkSize}`,
        );

        // Fetch sequentially to debug easily (Promise.all handles parallel, but let's see which one fails)
        for (let i = 0; i < batches.length; i++) {
          const batchIds = batches[i];
          
          const { data: batchData, error: batchError } = await supabase
            .from("claims_progress")
            .select("*")
            .in("subcategory_id", batchIds);

          if (batchError) {
            logError(`Batch ${i + 1} FAILED:`, batchError);
                      } else {
            console.log(
              `Batch ${i + 1} success. items: ${batchData?.length}`,
            );
            if (batchData) {
              progressData = [...progressData, ...batchData];
            }
          }
        }
        console.groupEnd();
      }

      // Fetch variations
      const { data: variationsData, error: variationsError } = await supabase
        .from("claims_variations")
        .select("*")
        .eq("project_id", projectId)
        .order("variation_date", { ascending: false });

      if (variationsError) throw variationsError;

      // SEQ-169: hire register items carry audit notes alongside variations, and
      // both live in claims_entity_comments keyed by entity type and entity id.
      const { data: hireRegisterRows } = await entityCommentsDb
        .from("claims_hire_register")
        .select("id, audit_flag, item, docket_no")
        .eq("project_id", projectId);

      const hireFlagMap: Record<string, CommentFlag> = {};
      const hireLabelMap: Record<string, string> = {};
      for (const row of hireRegisterRows || []) {
        hireFlagMap[row.id] = normaliseFlag(row.audit_flag);
        hireLabelMap[row.id] = row.item || row.docket_no || "Hire item";
      }
      setHireAuditFlags(hireFlagMap);
      setHireItemLabels(hireLabelMap);

      const varIds = (variationsData || []).map((v) => v.id);
      const hireIds = (hireRegisterRows || []).map((row) => row.id);

      // Fetch unread comment counts
      if (varIds.length > 0 || hireIds.length > 0) {
        const { data: authData } = await supabase.auth.getUser();
        const currentUser = authData?.user;

        if (currentUser) {
          const entityIds = [...varIds, ...hireIds];

          // Fetch comment metadata
          const { data: commentsMeta } = await entityCommentsDb
            .from(ENTITY_COMMENTS_TABLE)
            .select("entity_type, entity_id, created_at, user_id")
            .in("entity_id", entityIds);

          // Fetch user's view timestamps
          const { data: userViews } = await entityCommentsDb
            .from(ENTITY_VIEWS_TABLE)
            .select("entity_type, entity_id, last_read_at")
            .eq("user_id", currentUser.id)
            .in("entity_id", entityIds);

          const comments = toCommentMeta(commentsMeta);
          const views = toViewMeta(userViews);

          setUnreadVariationCounts(
            computeUnreadCounts({
              entityType: "variation",
              entityIds: varIds,
              comments,
              views,
              currentUserId: currentUser.id,
            }),
          );

          setUnreadHireCounts(
            computeUnreadCounts({
              entityType: "hire_item",
              entityIds: hireIds,
              comments,
              views,
              currentUserId: currentUser.id,
            }),
          );

          // Compute unread project comms count
          const { data: notesMeta } = await supabase
            .from("claims_project_notes")
            .select("id, created_at, user_id")
            .eq("project_id", projectId);

          const { data: noteView } = await supabase
            .from("claims_project_note_views")
            .select("last_read_at")
            .eq("project_id", projectId)
            .eq("user_id", currentUser.id)
            .single();

          const noteLastRead = noteView ? new Date(noteView.last_read_at).getTime() : 0;
          const unreadNotes = (notesMeta || []).filter((n) => {
            const isOtherUser = n.user_id !== currentUser.id;
            const isNewer = new Date(n.created_at || 0).getTime() > noteLastRead;
            return isOtherUser && isNewer;
          }).length;

          setUnreadNotesCount(unreadNotes);
        }
      }

      // Fetch claims reports to get total retention
      const { data: reportsData } = await supabase
        .from("claims_reports")
        .select("retention_amount, retention_received_amount, reconciliation_summary")
        .eq("project_id", projectId);

      const calculatedRetentionTotal = (reportsData || []).reduce(
        (sum, r) => sum + getOperationalRetentionAmount(r),
        0,
      );
      setRetentionTotal(calculatedRetentionTotal);

      const calculatedRetentionReceived = (reportsData || []).reduce(
        (sum, r) => sum + (Number(r.retention_received_amount) || 0),
        0,
      );
      setRetentionReceived(calculatedRetentionReceived);

      console.log("[ProjectDetail] Setting project state");
      setProject({
        ...projectData,
        categories: repairedCategories,
      });
      setProgress(progressData || []);
      setVariations(variationsData || []);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
      setDiscounts((discountsData as any) || []);
    } catch (err) {
      logError("[ProjectDetail] Error fetching project:", err);
    } finally {
      setLoading(false);
    }
   
  }, [projectId]);

  // Lightweight re-fetch for variations only (used when PaidByBuilder updates approved_amount)
  const refreshVariations = useCallback(async () => {
    if (!projectId) return;
    const { data } = await supabase
      .from("claims_variations")
      .select("*")
      .eq("project_id", projectId)
      .order("variation_date", { ascending: false });
    if (data) setVariations(data);
  }, [projectId]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  // Publish project name to breadcrumb context so breadcrumbs show name instead of UUID
  useEffect(() => {
    if (project?.job_location && projectId) {
      setLabel(projectId, project.job_location);
    }
    return () => {
      if (projectId) {
        removeLabel(projectId);
      }
    };
  }, [project?.job_location, projectId, setLabel, removeLabel]);

  // Lightweight refresh for retention totals (called from claims tab after payment save)
  const refreshRetentionTotals = useCallback(async () => {
    if (!projectId) return;
    const { data } = await supabase
      .from("claims_reports")
      .select("retention_amount, retention_received_amount, reconciliation_summary")
      .eq("project_id", projectId);
    const total = (data || []).reduce((s, r) => s + getOperationalRetentionAmount(r), 0);
    const received = (data || []).reduce((s, r) => s + (Number(r.retention_received_amount) || 0), 0);
    setRetentionTotal(total);
    setRetentionReceived(received);
    // Also refresh the claims table so per-row retention cells update
    setReportsRefreshKey((k) => k + 1);
  }, [projectId]);

  const stopSyncPolling = () => {
    if (syncPollRef.current) clearInterval(syncPollRef.current);
    if (syncTimerRef.current) clearInterval(syncTimerRef.current);
    syncPollRef.current = null;
    syncTimerRef.current = null;
  };

  // Helper: start polling sync status from DB
  const startSyncPolling = (pid: string, startedAt?: string | null) => {
    stopSyncPolling();
    syncPollErrorCountRef.current = 0;

    // Start elapsed timer
    const parsedStartedAt = startedAt ? Date.parse(startedAt) : Number.NaN;
    const started = Number.isFinite(parsedStartedAt)
      ? parsedStartedAt
      : Date.now();
    setSyncStartedAt(new Date(started).toISOString());
    setSyncElapsed(Math.max(0, Math.round((Date.now() - started) / 1000)));
    syncTimerRef.current = setInterval(() => {
      setSyncElapsed(Math.max(0, Math.round((Date.now() - started) / 1000)));
    }, 1000);

    // Poll DB every 4 seconds
    syncPollRef.current = setInterval(async () => {
      try {
        const { data: proj, error } = await supabase
          .from("claims_projects")
          .select("sync_status, sync_started_at, last_dashpivot_sync_at")
          .eq("id", pid)
          .single();

        if (error) throw error;
        if (!proj) return;
        syncPollErrorCountRef.current = 0;

        const polledLockState = classifySyncLock({
          status: proj.sync_status,
          startedAt: proj.sync_started_at,
        });

        if (polledLockState === "stale") {
          setSyncStatus("idle");
          setSyncStartedAt(null);
          setSyncElapsed(0);
          stopSyncPolling();
          addToast({
            type: "warning",
            title: "Previous Sync Interrupted",
            message: "The previous sync did not finish. You can safely retry now.",
            duration: 6000,
          });
          return;
        }

        if (proj.sync_status === 'idle') {
          // Sync finished, stop polling, refresh data
          setSyncStatus('idle');
          stopSyncPolling();

          // Refresh project's last_dashpivot_sync_at
          setProject((prev) => prev ? { ...prev, last_dashpivot_sync_at: proj.last_dashpivot_sync_at } : prev);

          // Refresh variations
          const { data: freshVariations } = await supabase
            .from("claims_variations")
            .select("*")
            .eq("project_id", pid)
            .order("variation_date", { ascending: false });
          if (freshVariations) {
            setVariations(freshVariations);
          }

          addToast({
            type: "success",
            title: "Sync Complete",
            message: "Variations synced successfully from Dashpivot.",
            duration: 4000,
          });
        } else if (proj.sync_status === 'error') {
          setSyncStatus('error');
          stopSyncPolling();
        }
      } catch (e) {
        logError('[Sync poll] Error:', e);
        syncPollErrorCountRef.current += 1;
        if (syncPollErrorCountRef.current >= 3) {
          setSyncStatus("error");
          stopSyncPolling();
          addToast({
            type: "warning",
            title: "Unable to Check Sync Status",
            message: "Status checks failed. Dismiss this message and retry the sync.",
            duration: 6000,
          });
        }
      }
    }, 4000);
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopSyncPolling();
    };
  }, []);

  // On mount: check current sync_status from DB
  useEffect(() => {
    if (!projectId) return;
    const checkSyncStatus = async () => {
      const { data: proj, error } = await supabase
        .from("claims_projects")
        .select("sync_status, sync_started_at")
        .eq("id", projectId)
        .single();
      if (error) {
        logError("[Initial sync status] Error:", error);
        return;
      }
      if (proj?.sync_status === 'syncing') {
        const initialLockState = classifySyncLock({
          status: proj.sync_status,
          startedAt: proj.sync_started_at,
        });
        if (initialLockState === "stale") {
          setSyncStatus("idle");
          setSyncStartedAt(null);
          setSyncElapsed(0);
          return;
        }

        setSyncStatus('syncing');
        setSyncStartedAt(proj.sync_started_at);
        startSyncPolling(projectId, proj.sync_started_at);
      }
    };
    checkSyncStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Auto-sync variations when project is opened (runs once on mount)
  useEffect(() => {
    if (autoSyncRan.current || !projectId) return;
    autoSyncRan.current = true;

    const autoSync = async () => {
      // Check sync_status before invoking, skip if already syncing
      const { data: proj } = await supabase
        .from("claims_projects")
        .select("sync_status, sync_started_at")
        .eq("id", projectId)
        .single();

      const autoSyncLockState = classifySyncLock({
        status: proj?.sync_status,
        startedAt: proj?.sync_started_at,
      });

      if (autoSyncLockState === "active") {
        addToast({
          type: "info",
          title: "Sync In Progress",
          message: "A sync is already in progress for this project. Showing current data.",
          duration: 4000,
        });
        setSyncStatus('syncing');
        startSyncPolling(projectId, proj?.sync_started_at);
        return;
      }

      try {
        setSyncStatus('syncing');
        startSyncPolling(projectId);

        addToast({
          type: "info",
          title: "Syncing Variations",
          message: "Automatically syncing variations from Dashpivot...",
          duration: 3000,
        });

        // Fire-and-forget: polling is the sole mechanism for tracking sync completion.
        // We only catch errors here (network failures, already_syncing, atomic failure).
        supabase.functions.invoke(
          "sync-variations",
          { body: { projectId } },
        ).then(({ data, error }) => {
          if (error) {
            const isRateLimit = /429|rate.?limit|too many/i.test(error.message || "");
            if (isRateLimit) {
              // Rate limit = sync never started → stop polling
              setSyncStatus('error');
              if (syncPollRef.current) clearInterval(syncPollRef.current);
              if (syncTimerRef.current) clearInterval(syncTimerRef.current);
              syncPollRef.current = null;
              syncTimerRef.current = null;
              addToast({
                type: "warning",
                title: "Dashpivot Rate Limited",
                message: "Dashpivot is temporarily limiting requests. This is normal, data will sync on your next visit.",
                duration: 5000,
              });
            } else {
              // Gateway timeout / connection closed, sync may still be running.
              // DON'T kill polling, let it detect the final sync_status in DB.
              addToast({
                type: "info",
                title: "Sync Running in Background",
                message: "The sync is taking longer than usual and will complete in the background.",
                duration: 6000,
              });
            }
            return;
          }

          // Handle already_syncing response from edge function
          if (data?.status === 'already_syncing') {
            addToast({
              type: "info",
              title: "Sync In Progress",
              message: data.message || "A sync is already in progress.",
              duration: 4000,
            });
            // Keep polling, another tab/process is running the sync
            return;
          }

          // Handle atomic failure
          if (data?.success === false && data?.atomic) {
            setSyncStatus('error');
            if (syncPollRef.current) clearInterval(syncPollRef.current);
            if (syncTimerRef.current) clearInterval(syncTimerRef.current);
            syncPollRef.current = null;
            syncTimerRef.current = null;
            addToast({
              type: "error",
              title: "Sync Failed",
              message: data.message || "Sync failed. Your existing data was preserved. Please retry.",
              duration: 8000,
            });
            return;
          }

          // Removal guards report only through results[]. Surface them, or a
          // suppressed removal check and a growing flagged backlog stay invisible.
          for (const notice of filterNewNotices(extractRemovalNotices(data?.results))) {
            addToast({
              type: notice.kind === "skipped" ? "warning" : "info",
              title: notice.title,
              message: notice.message,
              // A suppressed removal check is actionable now; a backlog is a
              // standing condition, so it gets the shorter, quieter treatment.
              duration: notice.kind === "skipped" ? 12000 : 6000,
            });
          }

          // If we reach here, edge function returned success.
          // Polling will detect sync_status='idle' and refresh data automatically.
        }).catch((err) => {
          logError("[Auto-sync] Network error:", err);
          // DON'T kill polling, sync may still be running in the background.
          // The edge function's finally block will set sync_status to 'idle' when done.
          addToast({
            type: "info",
            title: "Sync Running in Background",
            message: "Connection interrupted but the sync may still be running. Waiting for completion...",
            duration: 6000,
          });
        });
      } catch (err) {
        logError("[Auto-sync] Error:", err);
        // DON'T kill polling, sync may still be running.
        addToast({
          type: "info",
          title: "Sync Running in Background",
          message: "Connection interrupted but the sync may still be running. Waiting for completion...",
          duration: 6000,
        });
      }
    };

    autoSync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (loading) {
    return (
      <div style={{ padding: "var(--spacing-lg)" }}>
        {/* Skeleton: Back button */}
        <div className="skeleton" style={{ width: 140, height: 36, marginBottom: "var(--spacing-md)", borderRadius: 8 }} />

        {/* Skeleton: Header row */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "var(--spacing-md)", marginBottom: "var(--spacing-xl)", flexWrap: "wrap" }}>
          <div>
            <div className="skeleton" style={{ width: 260, height: 28, marginBottom: 8, borderRadius: 6 }} />
            <div className="skeleton" style={{ width: 180, height: 16, borderRadius: 6 }} />
          </div>
          <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
            <div className="skeleton" style={{ width: 110, height: 36, borderRadius: 8 }} />
            <div className="skeleton" style={{ width: 110, height: 36, borderRadius: 8 }} />
          </div>
        </div>

        {/* Skeleton: Summary section (chart + cards) */}
        <div style={{ display: "grid", gridTemplateColumns: "minmax(200px, 280px) 1fr", gap: "var(--spacing-lg)", marginBottom: "var(--spacing-xl)" }}>
          {/* Doughnut placeholder */}
          <div className="card" style={{ padding: "var(--spacing-lg)", display: "flex", flexDirection: "column", alignItems: "center", gap: "var(--spacing-md)" }}>
            <div className="skeleton" style={{ width: 120, height: 14, borderRadius: 6 }} />
            <div className="skeleton-circle" style={{ width: 150, height: 150 }} />
            <div style={{ display: "flex", gap: 12 }}>
              <div className="skeleton" style={{ width: 50, height: 10, borderRadius: 4 }} />
              <div className="skeleton" style={{ width: 60, height: 10, borderRadius: 4 }} />
            </div>
          </div>
          {/* Financial cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--spacing-md)" }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="card" style={{ padding: "var(--spacing-md) var(--spacing-lg)" }}>
                <div className="skeleton" style={{ width: 140, height: 12, marginBottom: 8, borderRadius: 4 }} />
                <div className="skeleton" style={{ width: 180, height: 24, borderRadius: 6 }} />
              </div>
            ))}
          </div>
        </div>

        {/* Skeleton: Tab bar */}
        <div style={{ display: "flex", gap: "var(--spacing-sm)", marginBottom: "var(--spacing-xl)", borderBottom: "1px solid var(--border-color)", paddingBottom: "var(--spacing-sm)" }}>
          {[100, 90, 130, 110, 100, 80].map((w, i) => (
            <div key={i} className="skeleton" style={{ width: w, height: 36, borderRadius: 8 }} />
          ))}
        </div>

        {/* Skeleton: Content area */}
        <div className="card" style={{ padding: "var(--spacing-lg)" }}>
          <div className="skeleton" style={{ width: 160, height: 20, marginBottom: "var(--spacing-lg)", borderRadius: 6 }} />
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "var(--spacing-lg)" }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i}>
                <div className="skeleton" style={{ width: 80, height: 12, marginBottom: 6, borderRadius: 4 }} />
                <div className="skeleton" style={{ width: 140, height: 16, borderRadius: 4 }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div style={{ textAlign: "center", padding: "var(--spacing-2xl)" }}>
        <AlertTriangle
          size={48}
          style={{
            color: "var(--color-warning)",
            marginBottom: "var(--spacing-md)",
          }}
        />
        <h2>Project Not Found</h2>
        <p
          style={{
            color: "var(--text-muted)",
            marginBottom: "var(--spacing-lg)",
          }}
        >
          The project you're looking for doesn't exist or you don't have access.
        </p>
        <Link to="/projects" className="btn btn-primary">
          Back to Projects
        </Link>
      </div>
    );
  }

  const grossContractValue = calculateContractValue(project.categories);
  const totalDiscounts = discounts.reduce(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    (sum, d: any) => sum + Number(d.amount || 0),
    0,
  );
  const netContractValue = grossContractValue - totalDiscounts;

  // Filter variations: only 'valid' status, and never one sync has flagged as
  // gone from Dashpivot (that row may be a duplicate of one now living under a
  // different project, so counting it would double the money).
  const validVariations = variations
    .filter(isNotRemovedFromDashpivot)
    .filter((v) => !v.status || v.status === "valid");

  // What a progress claim is allowed to bill. The variations review tab keeps
  // the unfiltered list on purpose, so a flagged row stays visible to a human.
  const claimableVariations = excludeRemovedFromDashpivot(variations);

  // Total Approved = Debit amounts MINUS Credit amounts (credits offset existing variance)
  //
  // Capped at the docket's own total since 24 September 2026. Three production
  // variations carry an approved amount ABOVE their total, worth $38,710
  // between them, and counting the excess certified more than the docket is
  // worth and inflated this page's Revised Contract Value against the claim's.
  const totalApprovedVariations = validVariations.reduce((sum, v) => {
    const multiplier = v.debit_credit === "credit" ? -1 : 1;
    const docketTotal = Math.max(Number(v.total || 0), 0);
    const approved = Math.min(Math.max(Number(v.approved_amount || 0), 0), docketTotal);
    return sum + approved * multiplier;
  }, 0);
  const totalWaitingVariations = validVariations.reduce((sum, v) => {
    const multiplier = v.debit_credit === "credit" ? -1 : 1;
    const rawTotal = Number(v.total) || 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    const discount = Number((v as any).discount_percent) || 0;
    const effectiveTotal = rawTotal * (1 - discount / 100);
    const approved = Number(v.approved_amount) || 0;
    // Waiting is the remaining amount to be approved
    const waiting = effectiveTotal - approved;
    return sum + waiting * multiplier;
  }, 0);

  // Revised contract value includes approved variations (already has credits subtracted)
  const revisedContractValue = netContractValue + totalApprovedVariations;

  // Calculate total progress claims (all time)
  const totalProgressValue = progress.reduce(
    (sum, p) => sum + (Number(p.amount) || 0),
    0,
  );

  // Calculate retention ceiling
  const mode = project.retention_ceiling_mode || (project.retention_on_revised ? 'revised' : 'original');
  let retentionBase = grossContractValue;
  if (mode === 'revised') {
    retentionBase = revisedContractValue;
  } else if (mode === 'custom') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    retentionBase = Number((project as any).retention_custom_amount) || 0;
  }
  const retentionCeiling = retentionCeilingAmount(retentionBase, (project as { retention_ceiling_percent?: number | null }).retention_ceiling_percent);

  // Get progress for current month (may be used for future summary display)

  return (
    <div>
      {/* Edit Project Modal.

          SEQ-169 follow up: this is the only place EditProjectModal is
          rendered, so every way of opening it inherits the gate passed here.
          That includes the ?edit=1 deep link above, which is why the deep link
          itself is left open: the modal's non financial fields (job location,
          addresses, builder contacts, website copy, images, status) are edited
          by staff without financial access today, and only the claim timing
          block is withheld from them. */}
      <EditProjectModal
        project={project}
        isOpen={isEditProjectOpen}
        onClose={() => setIsEditProjectOpen(false)}
        onSaved={() => fetchProject(true)}
        canEditClaimTiming={hasFinancialAccess}
      />

      {/* Header */}
      <div style={{ marginBottom: "var(--spacing-xl)" }}>
        <button
          className="btn btn-ghost"
          onClick={() => navigate("/projects")}
          style={{ marginBottom: "var(--spacing-md)" }}
        >
          <ArrowLeft size={18} />
          Back to Projects
        </button>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "var(--spacing-md)",
            flexWrap: "wrap",
          }}
        >
          <div style={{ minWidth: 0, flex: 1 }}>
            <h1 style={{ marginBottom: "var(--spacing-xs)", wordBreak: "break-word", overflowWrap: "break-word" }}>
              {project.job_location}
            </h1>
            <p style={{ color: "var(--text-muted)", wordBreak: "break-word" }}>{project.job_address}</p>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-md)",
              flexShrink: 0,
              flexWrap: "wrap",
            }}
          >
            {/* Dynamic Sync Status Indicator */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: "var(--font-size-sm)",
                color: syncStatus === 'syncing' ? 'var(--color-info)' : syncStatus === 'error' ? 'var(--color-warning)' : 'var(--text-muted)',
                background: syncStatus === 'syncing' ? 'rgba(59, 130, 246, 0.08)' : syncStatus === 'error' ? 'rgba(245, 158, 11, 0.08)' : 'var(--bg-tertiary)',
                padding: "4px 10px",
                borderRadius: 20,
                border: `1px solid ${syncStatus === 'syncing' ? 'rgba(59, 130, 246, 0.3)' : syncStatus === 'error' ? 'rgba(245, 158, 11, 0.3)' : 'var(--border-color)'}`,
                transition: 'all 0.3s ease',
              }}
            >
              {syncStatus === 'syncing' ? (
                <>
                  <Loader2 size={14} className="animate-spin" style={{ color: 'var(--color-info)' }} />
                  <span>Syncing variations… ({syncElapsed}s)</span>
                </>
              ) : syncStatus === 'error' ? (
                <>
                  <AlertTriangle size={14} style={{ color: 'var(--color-warning)' }} />
                  <span>Sync failed</span>
                  <button
                    onClick={() => {
                      setSyncStatus('idle');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--color-info)',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-sm)',
                      textDecoration: 'underline',
                      padding: 0,
                    }}
                  >
                    Dismiss
                  </button>
                </>
              ) : project.last_dashpivot_sync_at ? (
                <>
                  <img
                    src="https://sitemate.com/wp-content/uploads/2018/08/Dashpivot-Logo-Playstore.png"
                    alt="Dashpivot"
                    style={{ width: 16, height: 16, objectFit: 'contain' }}
                  />
                  <span>
                    Last synced:{' '}
                    {new Date(project.last_dashpivot_sync_at).toLocaleString()}
                  </span>
                </>
              ) : (
                <>
                  <img
                    src="https://sitemate.com/wp-content/uploads/2018/08/Dashpivot-Logo-Playstore.png"
                    alt="Dashpivot"
                    style={{ width: 16, height: 16, objectFit: 'contain' }}
                  />
                  <span>Never synced</span>
                </>
              )}
            </div>
            <button
              className="btn btn-secondary"
              onClick={() => {
                const check = canCreateClaim();
                if (!check.allowed) {
                  addToast({ type: "error", title: "Action Blocked", message: check.reason || "Cannot create claims at this time." });
                  return;
                }
                setShowProgressModal(true);
              }}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                ...(isSuspended ? { opacity: 0.6 } : {}),
              }}
            >
              <FileText size={16} />
              Send Report
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => setIsEditProjectOpen(true)}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <Edit2 size={16} />
              Edit Project
            </button>
          </div>
        </div>
      </div>

      {/* Project Summary Section */}
      <ProjectSummarySection
        contractValue={grossContractValue}
        revisedContractValue={revisedContractValue}
        progressValue={totalProgressValue}
        totalApprovedVariations={totalApprovedVariations}
        totalWaitingVariations={totalWaitingVariations}
        discountsTotal={totalDiscounts}
        retentionTotal={retentionTotal}
        retentionCeiling={retentionCeiling}
        retentionMode={mode}
        retentionReceived={retentionReceived}
        projectId={projectId}
        onRetentionBulkSaved={refreshRetentionTotals}
      />

      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: "var(--spacing-sm)",
          marginBottom: "var(--spacing-xl)",
          borderBottom: "1px solid var(--border-color)",
          paddingBottom: "var(--spacing-sm)",
          paddingTop: "8px",
          overflowX: "auto",
          overflowY: "visible",
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
        }}
      >
        {[
          { id: "overview", label: "Overview", icon: FileText },
          (profile?.can_view_financials && !isSafeMode) ? { id: "status", label: "Status (P&L)", icon: PieChart } : null,
          { id: "claims", label: "Progress Claims", icon: TrendingUp },
          { id: "reports", label: "Claim Reports", icon: DollarSign },
          { id: "variations", label: "Variations", icon: DollarSign },
          { id: "notes", label: "Comms", icon: MessageSquare },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
          ((profile as any)?.can_view_vault) ? { id: "vault", label: "Vault", icon: Shield } : null,
        ].flatMap((tab) => {
          if (!tab) return [];
          const Icon = tab.icon;
          return [
            <button
              key={tab.id}
              className={`btn ${activeTab === tab.id ? "btn-primary" : "btn-ghost"}`}
              onClick={() => setActiveTab(tab.id as TabType)}
              style={{ position: "relative" }}
            >
              <Icon size={16} />
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.id === "notes" && unreadNotesCount > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: "-4px",
                    right: "-4px",
                    minWidth: "18px",
                    height: "18px",
                    borderRadius: "9px",
                    background: "linear-gradient(135deg, #f59e0b, #ef4444)",
                    color: "#fff",
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 4px",
                    boxShadow: "0 1px 4px rgba(239,68,68,0.3)",
                    animation: "badgePulse 2s infinite",
                  }}
                >
                  {unreadNotesCount > 99 ? "99+" : unreadNotesCount}
                </span>
              )}
            </button>
          ];
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && (
        <OverviewTab
          project={project}
          onEditCategories={() => setShowEditCategoriesModal(true)}
        />
      )}

      {activeTab === "status" && (
        <ProjectStatusTab
          project={project}
          onNavigateToClaims={(month, year) => {
            setSelectedMonth(month);
            setSelectedYear(year);
            setActiveTab("reports");
          }}
        />
      )}

      {activeTab === "claims" && (
        <ClaimsTab
          project={project}
          progress={progress}
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={setSelectedMonth}
          onYearChange={setSelectedYear}
          onProgressUpdate={(newProgress) => setProgress(newProgress)}
          revisedContractValue={revisedContractValue}
          totalWaitingVariations={totalWaitingVariations}
          highlightSubcategoryId={openSubcategoryId || undefined}
          highlightCategoryId={highlightCategoryId || undefined}
          initialViewMode={claimsViewParam === "range" ? "range" : undefined}
          onDeepLinkProcessed={() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("openSubcategory");
            newParams.delete("highlightCategory");
            newParams.delete("claimsView");
            setSearchParams(newParams, { replace: true });
          }}
        />
      )}

      {activeTab === "reports" && project && (
        <ProgressClaimReportsTab
          project={project}
          refreshKey={reportsRefreshKey}
          onRetentionUpdate={refreshRetentionTotals}
          onVariationsChanged={refreshVariations}
        />
      )}

      {activeTab === "variations" && (
        <VariationsTab
          project={project}
          variations={variations}
          onVariationsUpdate={setVariations}
          syncStatus={syncStatus}
          onSyncStart={() => {
            setSyncStatus('syncing');
            startSyncPolling(projectId!);
          }}
          unreadCounts={unreadVariationCounts}
          onMarkRead={(variationId) => {
            setUnreadVariationCounts((prev) => {
              const next = { ...prev };
              delete next[variationId];
              return next;
            });
          }}
          hireUnreadCounts={unreadHireCounts}
          hireAuditFlags={hireAuditFlags}
          onMarkHireRead={(hireItemId) => {
            setUnreadHireCounts((prev) => {
              const next = { ...prev };
              delete next[hireItemId];
              return next;
            });
          }}
          onHireFlagChange={(hireItemId, flag) => {
            setHireAuditFlags((prev) => ({ ...prev, [hireItemId]: flag }));
          }}
          openVariationId={openVariationId}
          openVariationDetailId={openVariationDetailId}
          onClearOpenVariation={() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("openVariation");
            setSearchParams(newParams, { replace: true });
          }}
          onClearOpenVariationDetail={() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("openVariationDetail");
            setSearchParams(newParams, { replace: true });
          }}
          openHireItemId={openHireItemId}
          hireItemLabels={hireItemLabels}
          onClearOpenHireItem={() => {
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("openHireItem");
            setSearchParams(newParams, { replace: true });
          }}
        />
      )}

      {activeTab === "notes" && projectId && profile?.id && (
        <ProjectNotesTab
          projectId={projectId}
          userId={profile.id}
          onUnreadCountChange={setUnreadNotesCount}
        />
      )}

      {activeTab === "vault" && projectId && profile?.id && (
        <ProjectVaultTab
          projectId={projectId}
          userId={profile.id}
        />
      )}

      {/* Edit Categories Modal */}
      {project && (
        <EditCategoriesModal
          isOpen={showEditCategoriesModal}
          onClose={() => setShowEditCategoriesModal(false)}
          projectId={project.id}
          categories={project.categories}
          onCategoriesUpdate={() => {
            setShowEditCategoriesModal(false);
            fetchProject(true);
          }}
        />
      )}

      {/* Progress Claim Modal - Moved to Page Level */}
      {project && (
        <ProgressClaimModal
          isOpen={showProgressModal}
          onClose={() => setShowProgressModal(false)}
          project={project}
          variations={claimableVariations}
          onReportGenerated={() => {
            setReportsRefreshKey((k) => k + 1);
            setActiveTab("reports");
          }}
        />
      )}
    </div>
  );
}

// Overview Tab Component
function OverviewTab({
  project,
  onEditCategories,
}: {
  project: ProjectWithDetails;
  onEditCategories: () => void;
}) {
  return (
    <div className="card">
      <h3 className="card-title" style={{ marginBottom: "var(--spacing-lg)" }}>
        Project Details
      </h3>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: "var(--spacing-lg)",
        }}
      >
        <div className="detail-grid-cell">
          <p
            style={{
              fontSize: "var(--font-size-sm)",
              color: "var(--text-muted)",
              marginBottom: 4,
            }}
          >
            Location
          </p>
          <p style={{ fontWeight: 500 }}>{project.job_location}</p>
        </div>
        <div className="detail-grid-cell">
          <p
            style={{
              fontSize: "var(--font-size-sm)",
              color: "var(--text-muted)",
              marginBottom: 4,
            }}
          >
            Address
          </p>
          <p style={{ fontWeight: 500 }}>{project.job_address}</p>
        </div>
        <div className="detail-grid-cell">
          <p
            style={{
              fontSize: "var(--font-size-sm)",
              color: "var(--text-muted)",
              marginBottom: 4,
            }}
          >
            Normal Rate
          </p>
          <p style={{ fontWeight: 500 }}>${project.normal_rate}/hr</p>
        </div>
        <div className="detail-grid-cell">
          <p
            style={{
              fontSize: "var(--font-size-sm)",
              color: "var(--text-muted)",
              marginBottom: 4,
            }}
          >
            Overtime Rate
          </p>
          <p style={{ fontWeight: 500 }}>${project.overtime_rate}/hr</p>
        </div>
        <div className="detail-grid-cell">
          <p
            style={{
              fontSize: "var(--font-size-sm)",
              color: "var(--text-muted)",
              marginBottom: 4,
            }}
          >
            Sat. Rate (Non-Contract)
          </p>
          <p style={{ fontWeight: 500 }}>${project.saturday_rate || 400}/hr</p>
        </div>
        <div className="detail-grid-cell">
          <p
            style={{
              fontSize: "var(--font-size-sm)",
              color: "var(--text-muted)",
              marginBottom: 4,
            }}
          >
            Sat. Rate (Contract OT)
          </p>
          <p style={{ fontWeight: 500 }}>
            ${project.saturday_contract_ot_rate || project.overtime_rate || 0}/hr
          </p>
        </div>
      </div>

      {project.project_details && (
        <div
          style={{
            marginTop: "var(--spacing-lg)",
            paddingTop: "var(--spacing-lg)",
            borderTop: "1px solid var(--border-color)",
          }}
        >
          <p
            style={{
              fontSize: "var(--font-size-sm)",
              color: "var(--text-muted)",
              marginBottom: 4,
            }}
          >
            Details
          </p>
          <p>{project.project_details}</p>
        </div>
      )}

      {/* Builder Details */}
      <div
        style={{
          marginTop: "var(--spacing-xl)",
          paddingTop: "var(--spacing-lg)",
          borderTop: "1px solid var(--border-color)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--spacing-md)",
            marginBottom: "var(--spacing-md)",
          }}
        >
          <h4 style={{ margin: 0 }}>Builder Details</h4>
          {(!project.builder_name && !project.builder_address && !project.builder_poc_name && !project.builder_email) && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: "12px",
                fontWeight: 500,
                padding: "4px 8px",
                borderRadius: "12px",
                backgroundColor: "rgba(245, 158, 11, 0.1)",
                color: "rgb(217, 119, 6)", // amber-600
              }}
            >
              <AlertTriangle size={14} />
              Missing Builder Details
            </span>
          )}
        </div>

        {(project.builder_name || project.builder_address || project.builder_poc_name || project.builder_email) && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "var(--spacing-lg)",
              background: "var(--surface-color)",
              padding: "var(--spacing-lg)",
              borderRadius: "var(--border-radius)",
              border: "1px solid var(--border-color)",
            }}
          >
            {project.builder_name && (
              <div className="detail-grid-cell">
                <p
                  style={{
                    fontSize: "var(--font-size-sm)",
                    color: "var(--text-muted)",
                    marginBottom: 4,
                  }}
                >
                  Builder Name
                </p>
                <p style={{ fontWeight: 500 }}>{project.builder_name}</p>
              </div>
            )}
            {project.builder_poc_name && (
              <div className="detail-grid-cell">
                <p
                  style={{
                    fontSize: "var(--font-size-sm)",
                    color: "var(--text-muted)",
                    marginBottom: 4,
                  }}
                >
                  Contact Person
                </p>
                <p style={{ fontWeight: 500 }}>{project.builder_poc_name}</p>
              </div>
            )}
            {project.builder_email && (
              <div className="detail-grid-cell">
                <p
                  style={{
                    fontSize: "var(--font-size-sm)",
                    color: "var(--text-muted)",
                    marginBottom: 4,
                  }}
                >
                  Email
                </p>
                <p style={{ fontWeight: 500 }}>{project.builder_email}</p>
              </div>
            )}
            {project.builder_address && (
              <div className="detail-grid-cell">
                <p
                  style={{
                    fontSize: "var(--font-size-sm)",
                    color: "var(--text-muted)",
                    marginBottom: 4,
                  }}
                >
                  Address
                </p>
                <p style={{ fontWeight: 500 }}>{project.builder_address}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Categories Summary */}
      <div style={{ marginTop: "var(--spacing-xl)", paddingTop: "var(--spacing-lg)", borderTop: "1px solid var(--border-color)" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "var(--spacing-md)",
          }}
        >
          <h4>Categories & Amounts</h4>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={onEditCategories}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Edit2 size={14} />
            Edit Categories
          </button>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Subcategories</th>
                <th style={{ textAlign: "right" }}>Total Amount</th>
              </tr>
            </thead>
            <tbody>
              {project.categories.map((cat) => {
                const total = cat.is_custom_amount_only
                  ? Number(cat.custom_amount || 0)
                  : cat.subcategories.reduce(
                    (sum, sub) => sum + Number(sub.amount || 0),
                    0,
                  );
                return (
                  <tr key={cat.id}>
                    <td style={{ fontWeight: 500 }}>{cat.name}</td>
                    <td style={{ color: "var(--text-muted)" }}>
                      {cat.is_custom_amount_only
                        ? "Fixed Amount"
                        : `${cat.subcategories.length} levels`}
                    </td>
                    <td data-seq-private="" style={{ textAlign: "right", fontWeight: 500 }}>
                      {formatCurrency(total)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Claim History Tooltip - shows which months/years claims were made
function ClaimHistoryTooltip({
  subcategoryId,
  progress,
  totalClaimed,
  totalPercentage,
}: {
  subcategoryId: string;
  progress: ClaimsProgress[];
  totalClaimed: number;
  totalPercentage: number;
}) {
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Filter and sort claims for this subcategory
  const claimHistory = progress
    .filter((p) => p.subcategory_id === subcategoryId && Number(p.amount) > 0)
    .sort((a, b) => {
      // Sort by year then month (most recent first)
      if (a.claim_year !== b.claim_year) return b.claim_year - a.claim_year;
      return b.claim_month - a.claim_month;
    });

  if (claimHistory.length === 0) {
    return (
      <span data-seq-private="">
        {formatCurrency(totalClaimed)} (
        <PercentageDisplay value={totalPercentage} />)
      </span>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
      }}
      onMouseEnter={(e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipPosition({ x: rect.right, y: rect.top });
        setShowTooltip(true);
      }}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <span data-seq-private="">
        {formatCurrency(totalClaimed)} (
        <PercentageDisplay value={totalPercentage} />)
      </span>
      <Info
        size={14}
        style={{
          color: "var(--color-info)",
          cursor: "help",
          opacity: 0.7,
          transition: "opacity 0.2s",
        }}
      />

      {showTooltip && (
        <div
          style={{
            position: "fixed",
            top: tooltipPosition.y - 8,
            left: tooltipPosition.x - 230,
            transform: "translateY(-100%)",
            background: "var(--bg-card)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--border-radius-md)",
            boxShadow: "var(--shadow-lg)",
            padding: "12px 16px",
            minWidth: 220,
            zIndex: 9999,
            animation: "fadeIn 0.15s ease",
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "var(--text-muted)",
              marginBottom: 8,
              fontWeight: 600,
            }}
          >
            Claim History
          </div>
          {claimHistory.map((claim, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "6px 0",
                borderBottom:
                  idx < claimHistory.length - 1
                    ? "1px solid var(--border-color-light)"
                    : "none",
                fontSize: 13,
              }}
            >
              <span style={{ color: "var(--text-secondary)" }}>
                {MONTH_NAMES[claim.claim_month - 1]} {claim.claim_year}
              </span>
              <span data-seq-private="" style={{ fontWeight: 500, color: "var(--text-primary)" }}>
                {formatCurrency(Number(claim.amount))}
              </span>
            </div>
          ))}
          <div
            style={{
              marginTop: 8,
              paddingTop: 8,
              borderTop: "1px solid var(--border-color)",
              display: "flex",
              justifyContent: "space-between",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            <span>Total</span>
            <span data-seq-private="" style={{ color: "var(--color-primary)" }}>
              {formatCurrency(totalClaimed)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

// Progress Input Component with smooth UX - local state + debounced save
function ProgressInput({
  initialValue,
  placeholder,
  type,
  subcategoryId,
  subcategoryAmount,
  onSave,
  width = 80,
}: {
  initialValue: number | null | undefined;
  placeholder: string;
  type: "percentage" | "amount";
  subcategoryId: string;
  subcategoryAmount: number;
  onSave: (
    subcategoryId: string,
    value: number,
    type: "percentage" | "amount",
    amount: number,
  ) => Promise<void>;
  width?: number;
}) {
  // Smart formatting - show whole number when exact (100 not 100.0)
  const formatValueClean = (
    val: number,
    inputType: "percentage" | "amount",
  ): string => {
    if (inputType === "amount") {
      return Math.round(val).toString();
    }
    // For percentage, check if it's a whole number
    const rounded = Math.round(val * 10) / 10;
    return Number.isInteger(rounded) ? rounded.toString() : rounded.toFixed(1);
  };

  const [localValue, setLocalValue] = useState(
    initialValue != null ? formatValueClean(initialValue, type) : "",
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update local value when prop changes (e.g., after save or month change)
  useEffect(() => {
    if (!isDirty) {
      setLocalValue(
        initialValue != null ? formatValueClean(initialValue, type) : "",
      );
    }
  }, [initialValue, type, isDirty]);

  // Debounced auto-save
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalValue(value);
    setIsDirty(true);

    // Clear previous timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Set new timeout for debounced save (500ms after typing stops)
    saveTimeoutRef.current = setTimeout(async () => {
      const numValue = parseFloat(value) || 0;
      setIsSaving(true);
      try {
        await onSave(subcategoryId, numValue, type, subcategoryAmount);
      } catch (error) {
        logError("Error saving:", error);
      } finally {
        setIsSaving(false);
        setIsDirty(false);
      }
    }, 500);
  };

  // Also save on blur if there are unsaved changes
  const handleBlur = async () => {
    if (isDirty && saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
      const numValue = parseFloat(localValue) || 0;
      setIsSaving(true);
      try {
        await onSave(subcategoryId, numValue, type, subcategoryAmount);
      } catch (error) {
        logError("Error saving:", error);
      } finally {
        setIsSaving(false);
        setIsDirty(false);
      }
    }
  };

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <input data-seq-private
        type="number"
        className="form-input"
        style={{
          width,
          textAlign: "right",
          padding: "6px 8px",
          borderColor: isDirty ? "var(--primary)" : undefined,
          transition: "border-color 0.2s ease",
        }}
        value={localValue}
        placeholder={placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
      />
      {isSaving && (
        <span
          style={{
            position: "absolute",
            right: 4,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--primary)",
            fontSize: 10,
            animation: "pulse 1s infinite",
          }}
        >
          ●
        </span>
      )}
    </div>
  );
}

// Claims Tab Component
function ClaimsTab({
  project,
  progress,
  selectedMonth,
  selectedYear,
  onMonthChange,
  onYearChange,
  onProgressUpdate,
  // These props are passed but used elsewhere - prefixed with underscore to suppress warning
  revisedContractValue: _revisedContractValue,
  totalWaitingVariations: _totalWaitingVariations,
  highlightSubcategoryId,
  highlightCategoryId,
  initialViewMode,
  onDeepLinkProcessed,
}: {
  project: ProjectWithDetails;
  progress: ClaimsProgress[];
  selectedMonth: number;
  selectedYear: number;
  onMonthChange: (month: number) => void;
  onYearChange: (year: number) => void;
  onProgressUpdate: (progress: ClaimsProgress[]) => void;
  revisedContractValue: number;
  totalWaitingVariations: number;
  highlightSubcategoryId?: string;
  highlightCategoryId?: string;
  initialViewMode?: "single" | "range";
  onDeepLinkProcessed?: () => void;
}) {
  // Suppress unused variable warnings
  void _revisedContractValue;
  void _totalWaitingVariations;
  const { user } = useAuth();
  const [saving, setSaving] = useState(false);
  // SEQ-247: Range is the default view, except at phone width (768px and
  // under, the same breakpoint the stylesheet uses) where the Range grid hides
  // its data columns behind a scroll. A deep link or the user's own click wins.
  const [viewMode, setViewMode] = useState<"single" | "range">(
    () =>
      initialViewMode ||
      (typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 768px)").matches
        ? "single"
        : "range"),
  );

  // Auto-switch to range view when deep-linked from notes mentions
  useEffect(() => {
    if (initialViewMode === "range") {
      setViewMode("range");
      // Clean up the deep link params after a short delay to allow ProgressRangeView to process
      if (onDeepLinkProcessed) {
        const timer = setTimeout(() => onDeepLinkProcessed(), 1500);
        return () => clearTimeout(timer);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialViewMode]);

  // Calculate total progress across all time for chart (currently unused but kept for future use)
  // const totalProgressValue = progress.reduce(
  //   (sum, p) => sum + (Number(p.amount) || 0),
  //   0,
  // );

  const handleProgressChange = async (
    subcategoryId: string,
    value: number,
    inputType: "percentage" | "amount",
    subcategoryAmount: number,
  ) => {
    setSaving(true);
    try {
      // Check if this is a fallback fixed amount ID that needs subcategory creation
      let actualSubcategoryId = subcategoryId;
      if (subcategoryId.startsWith("FIXED:")) {
        const categoryId = subcategoryId.replace("FIXED:", "");
        // Create the missing FIXED AMOUNT subcategory
        const { data: newSub, error: createError } = await supabase
          .from("claims_subcategories")
          .insert({
            organization_id: requireOrganizationId(),
            name: "FIXED AMOUNT",
            amount: subcategoryAmount,
            category_id: categoryId,
            sort_order: 0,
          })
          .select()
          .single();

        if (createError) {
          logError("Failed to create fixed amount subcategory:", createError);
          throw new Error(
            "Failed to create subcategory for fixed amount category",
          );
        }

        actualSubcategoryId = newSub.id;
        // Refresh the project data to get the new subcategory
        // This will be picked up by the parent component
      }

      // Calculate percentage and amount
      let percentage: number;
      let amount: number;

      if (inputType === "percentage") {
        percentage = value; // Allow negative and >100% values
        amount = (subcategoryAmount * percentage) / 100;
      } else {
        amount = value;
        percentage =
          subcategoryAmount > 0 ? (value / subcategoryAmount) * 100 : 0;
      }

      // Check if record exists
      const existingProgress = progress.find(
        (p) =>
          p.subcategory_id === actualSubcategoryId &&
          p.claim_month === selectedMonth &&
          p.claim_year === selectedYear,
      );

      if (existingProgress) {
        // Validation: If no change, skip update (prevents duplicate logs on blur without change)
        if (
          Math.abs((existingProgress.percentage ?? 0) - percentage) < 0.001 &&
          Math.abs((existingProgress.amount ?? 0) - amount) < 0.01 &&
          existingProgress.input_type === inputType
        ) {
          setSaving(false);
          return;
        }
      }

      // Use upsert to handle both insert and update (prevents 409 race conditions)
      const { data, error } = await supabase
        .from("claims_progress")
        .upsert(
          {
            organization_id: requireOrganizationId(),
            subcategory_id: actualSubcategoryId,
            claim_month: selectedMonth,
            claim_year: selectedYear,
            percentage,
            amount,
            input_type: inputType,
            updated_at: new Date().toISOString(),
          },
          {
            onConflict: "subcategory_id,claim_month,claim_year",
          },
        )
        .select()
        .single();

      if (error) throw error;

      if (data) {
        // Update local state robustly (handles both update and insert-that-became-update cases)
        const index = progress.findIndex(
          (p: ClaimsProgress) => p.id === data.id,
        );
        let newProgress: ClaimsProgress[];
        if (index >= 0) {
          newProgress = [...progress];
          newProgress[index] = data;
        } else {
          newProgress = [...progress, data];
        }
        onProgressUpdate(newProgress);

        // LOG ACTIVITY
        if (user) {
          const subName = project.categories
            .flatMap((c) => c.subcategories)
            .find((s) => s.id === actualSubcategoryId)?.name;

          const isUpdate = !!existingProgress;

          logActivity({
            projectId: project.id,
            userId: user.id,
            actionType: isUpdate ? "UPDATE" : "CREATE",
            entityType: "CLAIM",
            entityId: data.id,
            description: `${isUpdate ? "Updated claim" : "Claimed"} ${inputType === "percentage"
              ? percentage + "%"
              : formatCurrency(amount)
              } for ${subName || "item"}`,
            metadata: {
              subcategoryId: actualSubcategoryId,
              month: selectedMonth,
              year: selectedYear,
              oldPercentage: existingProgress?.percentage ?? 0,
              newPercentage: percentage,
              oldAmount: existingProgress?.amount ?? 0,
              newAmount: amount,
            },
          });
        }
      }
    } catch (err) {
      logError("Error saving progress:", err);
      // Show toast error to user
      const errorMessage =
        err instanceof Error ? err.message : "Failed to save progress";
      alert(errorMessage); // Simple alert for now - can be replaced with toast
    } finally {
      setSaving(false);
    }
  };

  const getProgressValue = (subcategoryId: string) => {
    return progress.find(
      (p) =>
        p.subcategory_id === subcategoryId &&
        p.claim_month === selectedMonth &&
        p.claim_year === selectedYear,
    );
  };

  // CSV Import state and handlers
  const { addToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [pendingValidation, setPendingValidation] =
    useState<ValidationResult | null>(null);

  const handleDownloadTemplate = () => {
    const categories = project.categories.map((cat) => ({
      name: cat.name,
      isFixedAmount: cat.is_custom_amount_only || false,
      customAmount: Number(cat.custom_amount) || 0,
      subcategories: (cat.subcategories || []).map((sub) => ({
        name: sub.name,
        amount: Number(sub.amount) || 0,
      })),
    }));

    // Generate months array: current month + 2 previous months
    const months: { month: number; year: number }[] = [];
    for (let i = 2; i >= 0; i--) {
      let m = selectedMonth - i;
      let y = selectedYear;
      if (m <= 0) {
        m += 12;
        y -= 1;
      }
      months.push({ month: m, year: y });
    }

    downloadProgressCSVTemplate(categories, months, project.job_location);
    addToast({
      type: "info",
      title: "Template Downloaded",
      message: `Fill in the percentage columns for each month and re-upload`,
    });
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const text = await file.text();
      const rows = parseProgressCSV(text);

      // Build categories lookup for validation (include custom/fixed amount categories)
      const categoriesForValidation = project.categories.map((cat) => ({
        name: cat.name,
        isFixedAmount: cat.is_custom_amount_only || false,
        customAmount: Number(cat.custom_amount) || 0,
        subcategories: (cat.subcategories || []).map((sub) => ({
          name: sub.name,
          id: sub.id,
          amount: Number(sub.amount) || 0,
        })),
      }));

      const validation = validateProgressCSV(
        rows,
        categoriesForValidation,
        selectedMonth,
        selectedYear,
      );

      // If validation has only errors and no valid entries, show toast error
      if (validation.valid.length === 0) {
        const errors = validation.errors
          .slice(0, 3)
          .map((e) => e.message)
          .join("; ");
        addToast({
          type: "error",
          title: "Import Failed",
          message:
            validation.errors.length > 0
              ? errors +
              (validation.errors.length > 3
                ? ` (+${validation.errors.length - 3} more)`
                : "")
              : "No entries with percentage > 0% found",
          duration: 8000,
        });
        return;
      }

      // Show confirmation modal
      setPendingValidation(validation);
      setShowImportModal(true);
    } catch (err) {
      addToast({
        type: "error",
        title: "CSV Parse Error",
        message:
          err instanceof Error ? err.message : "Failed to parse CSV file",
        duration: 8000,
      });
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingValidation) return;

    setShowImportModal(false);
    setSaving(true);

    // Show immediate feedback so user knows import is in progress
    addToast({
      type: "info",
      title: "Importing...",
      message: `Processing ${pendingValidation.valid.length} entries. Please wait...`,
      duration: 10000,
    });

    try {
      let savedCount = 0;
      const newProgress = [...progress];

      for (const { entry, subcategory_id } of pendingValidation.valid) {
        const existingProgress = newProgress.find(
          (p) =>
            p.subcategory_id === subcategory_id &&
            p.claim_month === entry.claim_month &&
            p.claim_year === entry.claim_year,
        );

        if (existingProgress) {
          const { error } = await supabase
            .from("claims_progress")
            .update({
              percentage: entry.percentage,
              amount: entry.amount,
              input_type: "percentage",
            })
            .eq("id", existingProgress.id);

          if (!error) {
            const idx = newProgress.findIndex(
              (p) => p.id === existingProgress.id,
            );
            if (idx >= 0) {
              newProgress[idx] = {
                ...newProgress[idx],
                percentage: entry.percentage,
                amount: entry.amount,
                input_type: "percentage",
              };
            }
            savedCount++;
          }
        } else {
          const { data, error } = await supabase
            .from("claims_progress")
            .insert({
              organization_id: requireOrganizationId(),
              subcategory_id,
              claim_month: entry.claim_month,
              claim_year: entry.claim_year,
              percentage: entry.percentage,
              amount: entry.amount,
              input_type: "percentage",
            })
            .select()
            .single();

          if (!error && data) {
            newProgress.push(data);
            savedCount++;
          }
        }
      }

      onProgressUpdate(newProgress);

      // Calculate total amount imported
      const totalAmount = pendingValidation.valid.reduce(
        (sum, v) => sum + v.entry.amount,
        0,
      );

      // Get distinct periods for feedback
      const periods = Array.from(
        new Set(
          pendingValidation.valid.map(
            (v) =>
              `${MONTH_NAMES[v.entry.claim_month - 1]} ${v.entry.claim_year}`,
          ),
        ),
      ).join(", ");

      addToast({
        type: "success",
        title: "Import Successful",
        message: `Imported ${savedCount} entries for ${periods}. Total: ${formatCurrency(
          totalAmount,
        )}`,
        duration: 5000,
      });
    } catch (err) {
      addToast({
        type: "error",
        title: "Import Failed",
        message: err instanceof Error ? err.message : "Database error occurred",
      });
    } finally {
      setSaving(false);
      setPendingValidation(null);
    }
  };

  return (
    <div style={{ overflow: "hidden", width: "100%" }}>
      {/* View Mode Toggle & Controls */}
      <div className="card" style={{ marginBottom: "var(--spacing-lg)" }}>
        <div
          style={{
            display: "flex",
            gap: "var(--spacing-md)",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          {/* View Mode Toggle */}
          <div style={{ display: "flex", gap: 4 }}>
            <button
              type="button"
              className={`btn btn-sm ${viewMode === "single" ? "btn-primary" : "btn-secondary"
                }`}
              onClick={() => setViewMode("single")}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <Calendar size={14} />
              Single Month
            </button>
            <button
              type="button"
              className={`btn btn-sm ${viewMode === "range" ? "btn-primary" : "btn-secondary"
                }`}
              onClick={() => setViewMode("range")}
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <LayoutGrid size={14} />
              Range View
            </button>
          </div>

          {/* Single Month Controls */}
          {viewMode === "single" && (
            <div
              style={{
                display: "flex",
                gap: "var(--spacing-md)",
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <label style={{ fontWeight: 500 }}>Period:</label>
              <select
                value={selectedMonth}
                onChange={(e) => onMonthChange(Number(e.target.value))}
                className="form-input"
                style={{ width: "auto", minWidth: 120 }}
              >
                {MONTH_NAMES.map((name, idx) => (
                  <option key={idx} value={idx + 1}>
                    {name}
                  </option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => onYearChange(Number(e.target.value))}
                className="form-input"
                style={{ width: "auto", minWidth: 100 }}
              >
                {[2023, 2024, 2025, 2026, 2027, 2028, 2029, 2030].map(
                  (year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ),
                )}
              </select>
              {saving && (
                <span
                  style={{
                    color: "var(--text-muted)",
                    fontSize: "var(--font-size-sm)",
                  }}
                >
                  Saving...
                </span>
              )}
            </div>
          )}

          {/* Import/Export buttons */}
          <div style={{ display: "flex", gap: "var(--spacing-sm)" }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={handleDownloadTemplate}
              style={{ display: "flex", alignItems: "center", gap: 4 }}
            >
              <Download size={14} />
              Template
            </button>
            <label
              className="btn btn-secondary btn-sm"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                cursor: "pointer",
              }}
            >
              <Upload size={14} />
              Import
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onClick={(e) => (e.currentTarget.value = "")}
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />
            </label>
          </div>
        </div>
      </div>

      {/* Range View Mode */}
      {viewMode === "range" && (
        <div style={{ overflow: "hidden", width: "100%", maxWidth: "100%" }}>
          <ProgressRangeView
            key={project.id}
            project={project}
            progress={progress}
            onProgressUpdate={onProgressUpdate}
            highlightSubcategoryId={highlightSubcategoryId}
            highlightCategoryId={highlightCategoryId}
          />
        </div>
      )}

      {/* Single Month View */}
      {viewMode === "single" && (
        <>
          {/* Total Claimed Summary */}
          <div
            className="card"
            style={{
              marginBottom: "var(--spacing-lg)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "var(--spacing-md) var(--spacing-lg)",
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              color: "white",
            }}
          >
            <div>
              <div style={{ fontSize: "0.85rem", opacity: 0.9 }}>
                Total Variance for {MONTH_NAMES[selectedMonth - 1]}{" "}
                {selectedYear}
              </div>
              <div data-seq-private="" style={{ fontSize: "1.75rem", fontWeight: 700 }}>
                {formatCurrency(
                  progress
                    .filter(
                      (p) =>
                        p.claim_month === selectedMonth &&
                        p.claim_year === selectedYear,
                    )
                    .reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
                )}
              </div>
            </div>
            <div
              style={{ textAlign: "right", opacity: 0.9, fontSize: "0.85rem" }}
            >
              {
                progress.filter(
                  (p) =>
                    p.claim_month === selectedMonth &&
                    p.claim_year === selectedYear &&
                    (Number(p.amount) || 0) > 0,
                ).length
              }{" "}
              item(s) claimed
            </div>
          </div>

          {/* Category Cards */}
          {project.categories.map((category) => (
            <div
              key={category.id}
              className="card"
              style={{ marginBottom: "var(--spacing-lg)" }}
            >
              <h4 style={{ marginBottom: "var(--spacing-md)" }}>
                {category.name}
              </h4>

              {category.is_custom_amount_only ? (
                /* Custom/Fixed Amount Category - render as single row table */
                (() => {
                  const customAmount = Number(category.custom_amount) || 0;
                  // For custom categories, we use the "FIXED AMOUNT" subcategory ID
                  const fixedSub = category.subcategories.find(
                    (s) => s.name.toUpperCase() === "FIXED AMOUNT",
                  );
                  const customProgressId = fixedSub
                    ? fixedSub.id
                    : `FIXED:${category.id}`; // Fallback (shouldn't be needed after migration)
                  const currentProgress = progress.find(
                    (p) =>
                      p.subcategory_id === customProgressId &&
                      p.claim_month === selectedMonth &&
                      p.claim_year === selectedYear,
                  );
                  const totalClaimed = progress
                    .filter((p) => p.subcategory_id === customProgressId)
                    .reduce((sum, p) => sum + Number(p.amount || 0), 0);
                  const totalPercentage =
                    customAmount > 0 ? (totalClaimed / customAmount) * 100 : 0;

                  let ragStatus = "green";
                  // Compare amounts directly to avoid floating point issues
                  if (totalClaimed > customAmount)
                    ragStatus = "red"; // Over Limit
                  else if (totalClaimed >= customAmount * 0.995)
                    ragStatus = "blue"; // Max Reached (99.5%+)
                  else if (totalClaimed > customAmount * 0.9)
                    ragStatus = "amber"; // Near Limit (90%+)

                  return (
                    <div className="table-container">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th style={{ textAlign: "right" }}>
                              Contract Value
                            </th>
                            <th style={{ textAlign: "right" }}>This Month %</th>
                            <th style={{ textAlign: "right" }}>This Month $</th>
                            <th style={{ textAlign: "right" }}>
                              Total Variance
                            </th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td>Fixed Amount</td>
                            <td data-seq-private="" style={{ textAlign: "right" }}>
                              {formatCurrency(customAmount)}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <ProgressInput
                                initialValue={currentProgress?.percentage}
                                placeholder="0"
                                type="percentage"
                                subcategoryId={customProgressId}
                                subcategoryAmount={customAmount}
                                onSave={handleProgressChange}
                                width={80}
                              />
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <ProgressInput
                                initialValue={currentProgress?.amount}
                                placeholder="0"
                                type="amount"
                                subcategoryId={customProgressId}
                                subcategoryAmount={customAmount}
                                onSave={handleProgressChange}
                                width={100}
                              />
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 500 }}>
                              <ClaimHistoryTooltip
                                subcategoryId={customProgressId}
                                progress={progress}
                                totalClaimed={totalClaimed}
                                totalPercentage={totalPercentage}
                              />
                            </td>
                            <td>
                              <span className={`rag-indicator ${ragStatus}`}>
                                <span className="rag-dot" />
                                {ragStatus === "green"
                                  ? "On Track"
                                  : ragStatus === "amber"
                                    ? "Near Limit"
                                    : ragStatus === "blue"
                                      ? "Max Reached"
                                      : "Over Limit"}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  );
                })()
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Level</th>
                        <th style={{ textAlign: "right" }}>Contract Value</th>
                        <th style={{ textAlign: "right" }}>This Month %</th>
                        <th style={{ textAlign: "right" }}>This Month $</th>
                        <th style={{ textAlign: "right" }}>Total Variance</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {category.subcategories.map((sub) => {
                        const currentProgress = getProgressValue(sub.id);
                        const totalClaimed = progress
                          .filter((p) => p.subcategory_id === sub.id)
                          .reduce((sum, p) => sum + Number(p.amount || 0), 0);
                        const totalPercentage =
                          Number(sub.amount) > 0
                            ? (totalClaimed / Number(sub.amount)) * 100
                            : 0;

                        let ragStatus = "green";
                        const budget = Number(sub.amount);
                        // Compare amounts directly to avoid floating point issues
                        if (totalClaimed > budget)
                          ragStatus = "red"; // Over Limit
                        else if (totalClaimed >= budget * 0.995)
                          ragStatus = "blue"; // Max Reached (99.5%+)
                        else if (totalClaimed > budget * 0.9)
                          ragStatus = "amber"; // Near Limit (90%+)

                        return (
                          <tr key={sub.id}>
                            <td>{sub.name}</td>
                            <td data-seq-private="" style={{ textAlign: "right" }}>
                              {formatCurrency(Number(sub.amount))}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <ProgressInput
                                initialValue={currentProgress?.percentage}
                                placeholder="0"
                                type="percentage"
                                subcategoryId={sub.id}
                                subcategoryAmount={Number(sub.amount)}
                                onSave={handleProgressChange}
                                width={80}
                              />
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <ProgressInput
                                initialValue={currentProgress?.amount}
                                placeholder="0"
                                type="amount"
                                subcategoryId={sub.id}
                                subcategoryAmount={Number(sub.amount)}
                                onSave={handleProgressChange}
                                width={100}
                              />
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 500 }}>
                              <ClaimHistoryTooltip
                                subcategoryId={sub.id}
                                progress={progress}
                                totalClaimed={totalClaimed}
                                totalPercentage={totalPercentage}
                              />
                            </td>
                            <td>
                              <span className={`rag-indicator ${ragStatus}`}>
                                <span className="rag-dot" />
                                {ragStatus === "green"
                                  ? "On Track"
                                  : ragStatus === "amber"
                                    ? "Near Limit"
                                    : ragStatus === "blue"
                                      ? "Max Reached"
                                      : "Over Limit"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* Import Confirmation Modal */}
      <Modal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          setPendingValidation(null);
        }}
        onConfirm={handleConfirmImport}
        title={`Import Progress Claims for ${pendingValidation && pendingValidation.valid.length > 0
          ? `${MONTH_NAMES[pendingValidation.valid[0].entry.claim_month - 1]
          } ${pendingValidation.valid[0].entry.claim_year}`
          : `${MONTH_NAMES[selectedMonth - 1]} ${selectedYear}`
          }`}
        confirmText="Import"
        confirmDisabled={
          saving || !pendingValidation || pendingValidation.valid.length === 0
        }
        size="xl"
      >
        {pendingValidation && (
          <div
            style={{
              display: "flex",
              gap: "var(--spacing-lg)",
              alignItems: "stretch",
            }}
          >
            {/* Left Sidebar: Summary Stats */}
            <div
              style={{
                flexShrink: 0,
                width: 140,
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing-sm)",
              }}
            >
              <div
                style={{
                  padding: "var(--spacing-md)",
                  background: "rgba(16, 185, 129, 0.1)",
                  borderRadius: "var(--border-radius-md)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "var(--font-size-2xl)",
                    fontWeight: 600,
                    color: "var(--color-success)",
                  }}
                >
                  {pendingValidation.valid.length}
                </div>
                <div
                  style={{
                    fontSize: "var(--font-size-sm)",
                    color: "var(--text-muted)",
                  }}
                >
                  Valid Entries
                </div>
              </div>
              <div
                style={{
                  padding: "var(--spacing-md)",
                  background:
                    pendingValidation.errors.length > 0
                      ? "rgba(239, 68, 68, 0.1)"
                      : "#f3f4f6",
                  borderRadius: "var(--border-radius-md)",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    fontSize: "var(--font-size-2xl)",
                    fontWeight: 600,
                    color:
                      pendingValidation.errors.length > 0
                        ? "var(--color-error)"
                        : "var(--text-muted)",
                  }}
                >
                  {pendingValidation.errors.length}
                </div>
                <div
                  style={{
                    fontSize: "var(--font-size-sm)",
                    color: "var(--text-muted)",
                  }}
                >
                  Errors
                </div>
              </div>
              <div
                style={{
                  padding: "var(--spacing-md)",
                  background: "rgba(59, 130, 246, 0.1)",
                  borderRadius: "var(--border-radius-md)",
                  textAlign: "center",
                }}
              >
                <div data-seq-private=""
                  style={{
                    fontSize: "var(--font-size-lg)",
                    fontWeight: 600,
                    color: "var(--color-primary)",
                  }}
                >
                  {formatCurrency(
                    pendingValidation.valid.reduce(
                      (sum, v) => sum + v.entry.amount,
                      0,
                    ),
                  )}
                </div>
                <div
                  style={{
                    fontSize: "var(--font-size-sm)",
                    color: "var(--text-muted)",
                  }}
                >
                  Total Amount
                </div>
              </div>
            </div>

            {/* Right Section: Table and Errors */}
            <div style={{ flexGrow: 1, minWidth: 0 }}>
              {/* Valid Entries Preview - Column-Based Month View */}
              {pendingValidation.valid.length > 0 &&
                (() => {
                  // Group entries by month/year and subcategory for column-based display
                  const monthSet = new Map<
                    string,
                    { month: number; year: number }
                  >();
                  const subcategoryMap = new Map<
                    string,
                    {
                      category: string;
                      subcategory: string;
                      monthData: Map<
                        string,
                        { percentage: number; amount: number }
                      >;
                    }
                  >();

                  pendingValidation.valid.forEach((v) => {
                    const monthKey = `${v.entry.claim_month}/${v.entry.claim_year}`;
                    monthSet.set(monthKey, {
                      month: v.entry.claim_month,
                      year: v.entry.claim_year,
                    });

                    const subKey = `${v.entry.category}|${v.entry.subcategory}`;
                    if (!subcategoryMap.has(subKey)) {
                      subcategoryMap.set(subKey, {
                        category: v.entry.category,
                        subcategory: v.entry.subcategory,
                        monthData: new Map(),
                      });
                    }
                    subcategoryMap.get(subKey)!.monthData.set(monthKey, {
                      percentage: v.entry.percentage,
                      amount: v.entry.amount,
                    });
                  });

                  // Sort months chronologically
                  const sortedMonths = Array.from(monthSet.entries())
                    .sort((a, b) => {
                      if (a[1].year !== b[1].year) return a[1].year - b[1].year;
                      return a[1].month - b[1].month;
                    })
                    .map(([key, val]) => ({ key, ...val }));

                  // Calculate totals per month
                  const monthTotals = new Map<string, number>();
                  sortedMonths.forEach((m) => monthTotals.set(m.key, 0));
                  subcategoryMap.forEach((sub) => {
                    sub.monthData.forEach((data, monthKey) => {
                      monthTotals.set(
                        monthKey,
                        (monthTotals.get(monthKey) || 0) + data.amount,
                      );
                    });
                  });

                  const MONTH_ABBREV = [
                    "",
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "May",
                    "Jun",
                    "Jul",
                    "Aug",
                    "Sep",
                    "Oct",
                    "Nov",
                    "Dec",
                  ];

                  // Helper to get RAG status
                  const getStatus = (totalPct: number) => {
                    if (totalPct > 100)
                      return {
                        text: "Over Limit",
                        color: "#ef4444",
                        bg: "rgba(239, 68, 68, 0.1)",
                      };
                    if (totalPct >= 90)
                      return {
                        text: "Near Limit",
                        color: "#f59e0b",
                        bg: "rgba(245, 158, 11, 0.1)",
                      };
                    return {
                      text: "On Track",
                      color: "#10b981",
                      bg: "rgba(16, 185, 129, 0.1)",
                    };
                  };

                  return (
                    <div
                      style={{
                        overflow: "auto",
                        maxHeight: 290,
                        border: "1px solid #e2e8f0",
                        borderRadius: "var(--border-radius-md)",
                      }}
                    >
                      <table
                        className="table"
                        style={{
                          fontSize: "var(--font-size-sm)",
                          marginBottom: 0,
                          borderCollapse: "separate",
                          borderSpacing: 0,
                        }}
                      >
                        <thead
                          style={{ position: "sticky", top: 0, zIndex: 20 }}
                        >
                          <tr>
                            {/* Frozen left columns */}
                            <th
                              style={{
                                position: "sticky",
                                left: 0,
                                background: "#f8fafc",
                                zIndex: 21,
                                width: 80,
                                minWidth: 80,
                                maxWidth: 80,
                                boxSizing: "border-box",
                                padding: "8px 6px",
                                borderRight: "1px solid #e2e8f0",
                              }}
                            >
                              Category
                            </th>
                            <th
                              style={{
                                position: "sticky",
                                left: 80,
                                background: "#f8fafc",
                                zIndex: 21,
                                width: 100,
                                minWidth: 100,
                                maxWidth: 100,
                                boxSizing: "border-box",
                                padding: "8px 6px",
                                borderRight: "1px solid #e2e8f0",
                              }}
                            >
                              Subcategory
                            </th>
                            <th
                              style={{
                                position: "sticky",
                                left: 180,
                                background: "#f8fafc",
                                zIndex: 21,
                                width: 95,
                                minWidth: 95,
                                maxWidth: 95,
                                boxSizing: "border-box",
                                padding: "8px 6px",
                                textAlign: "right",
                                borderRight: "1px solid #e2e8f0",
                              }}
                            >
                              Contract Value
                            </th>
                            <th
                              style={{
                                position: "sticky",
                                left: 275,
                                background: "#f8fafc",
                                zIndex: 21,
                                width: 85,
                                minWidth: 85,
                                maxWidth: 85,
                                boxSizing: "border-box",
                                padding: "8px 6px",
                                textAlign: "center",
                                borderRight: "2px solid #cbd5e1",
                              }}
                            >
                              Status
                            </th>
                            {/* Scrollable month columns with totals in header */}
                            {sortedMonths.map((m) => (
                              <th
                                key={m.key}
                                style={{
                                  background: "#f8fafc",
                                  textAlign: "center",
                                  minWidth: 80,
                                  padding: "4px 6px",
                                }}
                              >
                                <div style={{ fontWeight: 600 }}>
                                  {MONTH_ABBREV[m.month]} {m.year}
                                </div>
                                <div data-seq-private=""
                                  style={{
                                    fontSize: 10,
                                    color: "var(--color-primary)",
                                    fontWeight: 600,
                                    marginTop: 2,
                                  }}
                                >
                                  {formatCurrency(monthTotals.get(m.key) || 0)}
                                </div>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Array.from(subcategoryMap.values()).map((sub, i) => {
                            const firstEntry = pendingValidation.valid.find(
                              (v) =>
                                v.entry.category === sub.category &&
                                v.entry.subcategory === sub.subcategory,
                            );
                            const contractValue =
                              firstEntry?.subcategory_amount || 0;
                            let totalPct = 0;
                            sub.monthData.forEach((data) => {
                              totalPct += data.percentage;
                            });
                            const status = getStatus(totalPct);

                            return (
                              <tr key={i}>
                                {/* Frozen left columns */}
                                <td
                                  style={{
                                    position: "sticky",
                                    left: 0,
                                    background: "#fff",
                                    zIndex: 1,
                                    width: 80,
                                    minWidth: 80,
                                    maxWidth: 80,
                                    boxSizing: "border-box",
                                    padding: "8px 6px",
                                    whiteSpace: "nowrap",
                                    borderRight: "1px solid #e2e8f0",
                                  }}
                                >
                                  {sub.category}
                                </td>
                                <td
                                  style={{
                                    position: "sticky",
                                    left: 80,
                                    background: "#fff",
                                    zIndex: 1,
                                    width: 100,
                                    minWidth: 100,
                                    maxWidth: 100,
                                    boxSizing: "border-box",
                                    padding: "8px 6px",
                                    whiteSpace: "nowrap",
                                    borderRight: "1px solid #e2e8f0",
                                  }}
                                >
                                  {sub.subcategory}
                                </td>
                                <td data-seq-private=""
                                  style={{
                                    position: "sticky",
                                    left: 180,
                                    background: "#fff",
                                    zIndex: 1,
                                    width: 95,
                                    minWidth: 95,
                                    maxWidth: 95,
                                    boxSizing: "border-box",
                                    padding: "8px 6px",
                                    textAlign: "right",
                                    fontWeight: 500,
                                    borderRight: "1px solid #e2e8f0",
                                  }}
                                >
                                  {formatCurrency(contractValue)}
                                </td>
                                <td
                                  style={{
                                    position: "sticky",
                                    left: 275,
                                    background: "#fff",
                                    zIndex: 1,
                                    width: 85,
                                    minWidth: 85,
                                    maxWidth: 85,
                                    boxSizing: "border-box",
                                    padding: "8px 6px",
                                    textAlign: "center",
                                    borderRight: "2px solid #cbd5e1",
                                  }}
                                >
                                  <span
                                    style={{
                                      display: "inline-block",
                                      padding: "2px 6px",
                                      borderRadius: 12,
                                      fontSize: 10,
                                      fontWeight: 600,
                                      color: status.color,
                                      background: status.bg,
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {status.text}
                                  </span>
                                </td>
                                {/* Month columns */}
                                {sortedMonths.map((m) => {
                                  const data = sub.monthData.get(m.key);
                                  if (!data)
                                    return (
                                      <td
                                        key={m.key}
                                        style={{
                                          textAlign: "center",
                                          color: "#94a3b8",
                                          padding: "8px 6px",
                                        }}
                                      >
                                        -
                                      </td>
                                    );
                                  return (
                                    <td
                                      key={m.key}
                                      style={{
                                        textAlign: "center",
                                        padding: "8px 6px",
                                      }}
                                    >
                                      <div style={{ fontWeight: 500 }}>
                                        {data.percentage % 1 === 0
                                          ? `${data.percentage}%`
                                          : `${data.percentage.toFixed(1)}%`}
                                      </div>
                                      <div data-seq-private=""
                                        style={{
                                          fontSize: 11,
                                          color: "#64748b",
                                        }}
                                      >
                                        {formatCurrency(data.amount)}
                                      </div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

              {/* Errors */}
              {pendingValidation.errors.length > 0 && (
                <div>
                  <h4
                    style={{
                      marginBottom: "var(--spacing-sm)",
                      color: "var(--color-error)",
                    }}
                  >
                    Errors (will be skipped)
                  </h4>
                  <div
                    style={{
                      maxHeight: 150,
                      overflowY: "auto",
                      background: "rgba(239, 68, 68, 0.05)",
                      borderRadius: "var(--border-radius-md)",
                      padding: "var(--spacing-sm)",
                    }}
                  >
                    {pendingValidation.errors.map((err, i) => (
                      <div
                        key={i}
                        style={{
                          fontSize: "var(--font-size-sm)",
                          color: "var(--color-error)",
                          marginBottom: 4,
                        }}
                      >
                        <strong>Row {err.row}:</strong> {err.message}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

// Variations Tab Component
// --- Variation Detail Modal ---

interface VariationDetailModalProps {
  groupedVariation: GroupedVariation;
  onClose: () => void;
  onApprovalChange: (
    variationId: string,
    approvedAmount: number,
    total: number,
  ) => Promise<void>;
}

interface VariationDetailModalProps {
  groupedVariation: GroupedVariation;
  project: ProjectWithDetails;
  onClose: () => void;
  onApprovalChange: (
    variationId: string,
    approvedAmount: number,
    total: number,
  ) => Promise<void>;
}

// ─── Date/Time formatting helpers for Dashpivot ISO values ───
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Format ISO date "2026-05-23" → "23 May" (or "23 May 2026" if different year) */
function formatISODate(iso: string | undefined | null): string {
  if (!iso) return "—";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso; // Not ISO, return as-is
  const [y, m, d] = parts;
  const month = SHORT_MONTHS[parseInt(m, 10) - 1] || m;
  const day = parseInt(d, 10);
  const currentYear = new Date().getFullYear().toString();
  return y === currentYear ? `${day} ${month}` : `${day} ${month} ${y}`;
}

/** Format ISO time "06:00:00" → "6:00 AM" */
function formatISOTime(iso: string | undefined | null): string {
  if (!iso) return "—";
  const parts = iso.split(":");
  if (parts.length < 2) return iso;
  let hr = parseInt(parts[0], 10);
  const min = parts[1];
  const ampm = hr >= 12 ? "PM" : "AM";
  if (hr === 0) hr = 12;
  else if (hr > 12) hr -= 12;
  return `${hr}:${min} ${ampm}`;
}

function VariationDetailModal({
  groupedVariation,
  project,
  onClose,
  onApprovalChange,
}: VariationDetailModalProps) {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { addToast } = useToast();
  // PDF Export state
  const [exporting, setExporting] = useState(false);
  const [exportStatus, setExportStatus] = useState<string>("");

  // Use the first item for "Key Details" (App ID, Form ID, Status, etc. are shared)
  const firstItem = groupedVariation.items[0];
  const variationStatus = (groupedVariation.status || 'valid').toLowerCase();
  const isNonValid = variationStatus === 'invalid' || variationStatus === 'pending';

  // Combined Approved Amount State
  // We initialize with the approved amount from the first item, as sync/logic stores it there.
  const [approvedAmount, setApprovedAmount] = useState<string>(
    (firstItem?.approved_amount || 0).toString(),
  );

  // Parse photos once for the group
  const groupPhotos = useMemo(() => {
    if (!firstItem?.photos) return [];
    if (Array.isArray(firstItem.photos)) return firstItem.photos;
    if (typeof firstItem.photos === "string") {
      try {
        return JSON.parse(firstItem.photos);
        // eslint-disable-next-line no-empty -- auto-suppressed for safety
      } catch { }
    }
    return [];
  }, [firstItem]);

  // Builder comments arrive as HTML from Dashpivot; render them as text only.
  const builderCommentsText = useMemo(
    () => htmlToPlainText(firstItem?.builder_comments),
    [firstItem?.builder_comments],
  );

  const handleApproveFull = async () => {
    if (!firstItem) return;
    // Use Math.abs() to ensure approved amount is always positive
    // groupedVariation.totalAmount is already the effective (discounted) amount
    const effectiveAmount = Math.abs(groupedVariation.totalAmount || 0);

    setApprovedAmount(effectiveAmount.toFixed(2));
    setSaving(true);
    try {
      // Pass effectiveAmount as both approved and total for waiting calculation
      // The waiting amount will be: effectiveAmount - effectiveAmount = 0
      await onApprovalChange(
        firstItem.id,
        effectiveAmount,
        effectiveAmount, // Use effective amount as total so waiting becomes 0
      );
      addToast({ type: "success", title: "Full Amount Approved" });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- auto-suppressed for safety
    } catch (e) {
      addToast({ type: "error", title: "Failed to approve full amount" });
    } finally {
      setSaving(false);
    }
  };

  const handleExportPdf = async () => {
    if (!firstItem?.app_id || !firstItem?.dashpivot_form_id) {
      addToast({
        type: "error",
        title: "Cannot export: Missing Dashpivot form info",
      });
      return;
    }

    setExporting(true);
    try {
      await exportFormAsPdf(
        firstItem.app_id,
        firstItem.dashpivot_form_id,
        groupedVariation.docket_no,
        (status) => setExportStatus(status),
      );
      addToast({
        type: "success",
        title: "PDF opened in new tab!",
        message:
          "If you don't see it, check if popups are blocked and allow them for this site.",
      });
    } catch (error) {
      logError("Export error:", error);
      addToast({
        type: "error",
        title: "Failed to export PDF",
        message:
          "Please try again. If PDF opens in new tab, check popup blocker settings.",
      });
    } finally {
      setExporting(false);
      setExportStatus("");
    }
  };

  const handleSaveApproval = async () => {
    if (!firstItem) return;
    setSaving(true);
    try {
      const val = parseFloat(approvedAmount) || 0;
      // We update the FIRST item, which represents the "Header" of the variation grouping in our sync logic
      await onApprovalChange(firstItem.id, val, groupedVariation.totalAmount);
      addToast({ type: "success", title: "Approved Amount Saved" });
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- auto-suppressed for safety
    } catch (e) {
      addToast({ type: "error", title: "Failed to save" });
    } finally {
      setSaving(false);
    }
  };

  const isCredit = firstItem?.debit_credit === "credit";

  // Calculate Materials Total
  // Using the materials list from the first item (where it is stored)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
  const materials = (firstItem?.materials as any[]) || [];
  const totalMaterialCost = materials.reduce(
    (acc, m) => acc + (m.total || 0),
    0,
  );

  // v29+ Hire Items data
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSONB columns not strongly typed
  const hireItems: any[] = (firstItem as any)?.hire_items && Array.isArray((firstItem as any).hire_items)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSONB columns not strongly typed
    ? (firstItem as any).hire_items
    : [];

  // Calculate running weeks for open-ended hire items
  // MUST match server formula: diffMs / (1000 * 60 * 60 * 24 * 7), fractional, no ceiling
  const getRunningWeeks = (startDate: string): number => {
    if (!startDate) return 0;
    const start = new Date(startDate);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    return Math.max(0, diffMs / (7 * 24 * 60 * 60 * 1000));
  };

  // Discount
  const discountPercent = Number(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    (groupedVariation.items[0] as any)?.discount_percent,
  );

  // DISPLAY ONLY. hi.total is the raw undiscounted value sync writes into the
  // hire_items JSONB, so on a discounted variation this panel used to show the
  // full amount while the register, the claim preview and the PDF all showed the
  // discounted one. The stored claims_variations.hire_items_total column is
  // deliberately left raw: getWorksAmounts in src/lib/variationClaimPreview.ts
  // reads that column, and discounting it would inflate worksRawTotal and
  // over-bill every works line.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSONB columns not strongly typed
  const getHireItemTotal = (hi: any): number => {
    // If there's an explicit total and it's non-zero, use it
    if (hi.total && parseFloat(hi.total) > 0) {
      return applyHireDiscount(parseFloat(hi.total), discountPercent);
    }
    // For open-ended: calculate running total = weeks * levels * qty * cost_per_week
    // Matches server: costPerWeek * qty * levels * effectiveWeeks
    if (!hi.end_date) {
      const weeks = getRunningWeeks(hi.start_date);
      const levels = parseFloat(hi.levels) || 1;
      const qty = parseFloat(hi.qty) || 1;
      const cost = parseFloat(hi.cost || hi.manual_cost) || 0;
      return applyHireDiscount(
        Math.round(cost * qty * levels * weeks * 100) / 100,
        discountPercent,
      );
    }
    return 0;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSONB columns not strongly typed
  const hireItemsTotal = hireItems.reduce((sum: number, hi: any) => sum + getHireItemTotal(hi), 0);

  // v29+ Saturday row-level detail
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSONB columns not strongly typed
  const satContractRowData: any[] = (firstItem as any)?.sat_contract_rows && Array.isArray((firstItem as any).sat_contract_rows)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSONB columns not strongly typed
    ? (firstItem as any).sat_contract_rows
    : [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSONB columns not strongly typed
  const satNonContractRowData: any[] = (firstItem as any)?.sat_non_contract_rows && Array.isArray((firstItem as any).sat_non_contract_rows)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- JSONB columns not strongly typed
    ? (firstItem as any).sat_non_contract_rows
    : [];

  // Determine if this is a legacy form (inline Saturday hours in day_labour_rows)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
  const dayLabourRows: any[] =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    (firstItem as any)?.day_labour_rows &&
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    Array.isArray((firstItem as any).day_labour_rows)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
      ? (firstItem as any).day_labour_rows
      : [];

  const isLegacyForm = dayLabourRows.some(
    (r) => parseFloat(r.hours_saturday) > 0,
  );

  // Saturday aggregate values
  const satContractOT =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    (firstItem as any)?.sat_contract_ot_qty || 0;
  const satNonContract = firstItem?.saturday_work_qty || 0;

  const hasSatContract = satContractOT > 0;
  const hasSatNonContract = satNonContract > 0 && !isLegacyForm;

  // Rates
  const normalRate = project.normal_rate || 0;
  const overtimeRate = project.overtime_rate || 0;
  const saturdayRate = project.saturday_rate || 400;
  const satContractOTRate = project.saturday_contract_ot_rate || project.overtime_rate || 0;

  // Financial calculations
  const satContractTotal = satContractOT * satContractOTRate;
  const satNCTotal = satNonContract * saturdayRate;

  // Waiting amount calculation
  const signedApproved = isCredit
    ? -(parseFloat(approvedAmount) || 0)
    : parseFloat(approvedAmount) || 0;
  const waitingValue = (groupedVariation.totalAmount || 0) - signedApproved;

  // Status badge class
  const statusLower = (groupedVariation.status || "").toLowerCase();
  const statusBadgeClass =
    statusLower === "valid"
      ? "var-badge--valid"
      : statusLower === "invalid"
        ? "var-badge--invalid"
        : "var-badge--pending";

  return (
    <div
      className="modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal var-detail-modal">
        {/* ─── HEADER ─── */}
        <div className="var-detail-header">
          <div className="var-detail-header-left">
            <div className="var-detail-title-row">
              <h3 className="var-detail-title">
                {groupedVariation.docket_no}
              </h3>
              <span
                className={`var-badge ${isCredit ? "var-badge--credit" : "var-badge--debit"}`}
              >
                {firstItem?.debit_credit || "Variance"}
              </span>
              <span className={`var-badge ${statusBadgeClass}`}>
                {(groupedVariation.status || "Pending").toUpperCase()}
              </span>
            </div>
            <div className="var-detail-date">
              {new Date(
                groupedVariation.variation_date + "T12:00:00",
              ).toLocaleDateString("en-AU", {
                weekday: "short",
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </div>
          </div>

          <div className="var-detail-actions">
            {firstItem?.app_id && firstItem?.dashpivot_form_id && (
              <a
                href={`https://dashpivot.com/apps/${firstItem.app_id}/forms/active/register/${firstItem.dashpivot_form_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                title="View or edit in Dashpivot"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                <ExternalLink size={16} />
                <span className="btn-label">View / Edit</span>
              </a>
            )}

            {groupedVariation.pdf_url && (
              <a
                href={groupedVariation.pdf_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                }}
              >
                <FileText size={16} />
                <span className="btn-label">View PDF</span>
              </a>
            )}

            <button
              className="btn btn-secondary"
              onClick={handleExportPdf}
              disabled={exporting}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                whiteSpace: "nowrap",
              }}
            >
              <Download size={16} />
              <span className="btn-label">
                {exporting ? exportStatus || "Exporting..." : "Export PDF"}
              </span>
            </button>

            <button className="btn btn-icon" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="var-detail-body">
          {/* Status Banner for non-valid variations */}
          {isNonValid && (
            <div className={`var-status-banner var-status-banner--${variationStatus}`}>
              <span className="var-status-banner-icon">
                {variationStatus === 'invalid' ? <XCircle size={18} /> : <Clock size={18} />}
              </span>
              <span>
                {variationStatus === 'invalid'
                  ? 'This variation is marked as Invalid, so it is excluded from financial reporting and progress claims.'
                  : 'This variation is Pending review, so it will not be included in financial reporting until marked as Valid.'}
              </span>
            </div>
          )}

          {/* SECTION 1: Financial Summary Strip */}
          <div className="var-finance-strip">
            {discountPercent > 0 && (
              <div
                style={{ gridColumn: "1 / -1", marginBottom: "4px" }}
              >
                <span className="var-badge var-badge--discount">
                  <Percent size={14} />
                  Discount Applied: {discountPercent}%
                </span>
              </div>
            )}

            <div className="var-finance-cell">
              <div className="var-finance-label">Total Variation</div>
              <div data-seq-private="" className="var-finance-value">
                {formatCurrency(groupedVariation.totalAmount)}
              </div>
            </div>

            <div className="var-finance-cell">
              <div className="var-finance-label">Approved</div>
              <div data-seq-private=""
                className={`var-finance-value ${parseFloat(approvedAmount) > 0 ? "var-finance-value--success" : ""}`}
              >
                {formatCurrency(parseFloat(approvedAmount) || 0)}
              </div>
            </div>

            <div className="var-finance-cell">
              <div className="var-finance-label">Waiting Approval</div>
              <div data-seq-private=""
                className={`var-finance-value ${waitingValue > 0.01 ? "var-finance-value--warning" : "var-finance-value--success"}`}
              >
                {formatCurrency(waitingValue)}
              </div>
            </div>

            {/* Approval Actions: hidden for invalid/pending variations */}
            {!isNonValid && (
            <div className="var-finance-cell--actions">
              <div className="var-finance-input-group">
                <div className="var-finance-input-wrap">
                  <span className="currency-prefix">$</span>
                  <input data-seq-private
                    type="number"
                    value={approvedAmount}
                    onChange={(e) => setApprovedAmount(e.target.value)}
                    className="form-input"
                    placeholder="0.00"
                    step="0.01"
                  />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveApproval}
                  disabled={
                    saving ||
                    Math.abs(
                      (parseFloat(approvedAmount) || 0) -
                        (firstItem?.approved_amount || 0),
                    ) < 0.01
                  }
                  style={{
                    height: "42px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Save size={16} />
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>

              {Math.abs(
                (parseFloat(approvedAmount) || 0) -
                  (groupedVariation.totalAmount || 0),
              ) > 0.01 && (
                <button
                  type="button"
                  onClick={handleApproveFull}
                  className="btn btn-secondary"
                  title="Set to full estimated amount"
                  disabled={saving}
                  style={{
                    height: "42px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <CheckCircle2 size={16} />
                  <span className="btn-label">Approve Full</span>
                </button>
              )}
            </div>
            )}
          </div>

          {/* SECTION 2: Day Labour Table */}
          <div className="var-section var-section--blue">
            <div className="var-section-header var-section-header--blue">
              <LayoutGrid size={18} /> Day Labour
            </div>
            {hasNoDayLabour(groupedVariation.items, dayLabourRows) ? (
              <div className="var-section-empty">
                No day labour on this variation.
              </div>
            ) : (
              <table className="var-table">
                <thead className="thead--blue">
                  <tr>
                    <th>Description</th>
                    <th className="text-right">Normal (hrs)</th>
                    <th className="text-right">OT (hrs)</th>
                    {isLegacyForm && (
                      <th className="text-right">Sat-NC (hrs)</th>
                    )}
                    <th className="text-right">Subtotal</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // New schema: render from day_labour_rows JSON
                    if (dayLabourRows.length > 0) {
                      return dayLabourRows.map(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                        (dlRow: any, idx: number) => {
                          const hrs1 = dlRow.hours_normal || 0;
                          const hrs2 = dlRow.hours_ot || 0;
                          const sat = dlRow.hours_saturday || 0;
                          const normalCost = hrs1 * normalRate;
                          const otCost = hrs2 * overtimeRate;
                          const satCost = sat * saturdayRate;
                          const rowTotal =
                            normalCost + otCost + satCost;

                          return (
                            <tr key={idx}>
                              <td className="font-medium">
                                {dlRow.title
                                  ? formatISODate(dlRow.title)
                                  : `Day Labour Entry #${idx + 1}`}
                              </td>
                              <td className="text-right">
                                {hrs1 > 0 ? (
                                  <div>
                                    {hrs1}
                                    <div data-seq-private="" className="rate-hint">
                                      @ {formatCurrency(normalRate)}
                                    </div>
                                  </div>
                                ) : (
                                  "–"
                                )}
                              </td>
                              <td className="text-right">
                                {hrs2 > 0 ? (
                                  <div>
                                    {hrs2}
                                    <div data-seq-private="" className="rate-hint">
                                      @ {formatCurrency(overtimeRate)}
                                    </div>
                                  </div>
                                ) : (
                                  "–"
                                )}
                              </td>
                              {isLegacyForm && (
                                <td className="text-right">
                                  {sat > 0 ? (
                                    <div>
                                      {sat}
                                      <div className="rate-hint">
                                        @{" "}
                                        {formatCurrency(saturdayRate)}
                                      </div>
                                    </div>
                                  ) : (
                                    "–"
                                  )}
                                </td>
                              )}
                              <td data-seq-private="" className="text-right font-semibold">
                                {formatCurrency(rowTotal)}
                              </td>
                              <td>
                                <div className="remarks-cell">
                                  {dlRow.remarks || "–"}
                                </div>
                              </td>
                            </tr>
                          );
                        },
                      );
                    }

                    // Legacy fallback: use groupedVariation.items
                    return groupedVariation.items.map((item, idx) => {
                      const hrs1 = item.hours_lvl_1 || 0;
                      const hrs2 = item.hours_lvl_2 || 0;
                      const sat = item.saturday_work_qty || 0;
                     
                      const satCO =
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                        (item as any).sat_contract_ot_qty || 0;
                      const normalCost = hrs1 * normalRate;
                      const otCost = hrs2 * overtimeRate;
                      const satCost = sat * saturdayRate;
                      const satContractCost = satCO * satContractOTRate;
                      const rowTotal =
                        normalCost + otCost + satCost + satContractCost;

                      return (
                        <tr key={item.id}>
                          <td className="font-medium">
                            {item.title ||
                              `Day Labour Entry #${idx + 1}`}
                          </td>
                          <td className="text-right">
                            {hrs1 > 0 ? (
                              <div>
                                {hrs1}
                                <div data-seq-private="" className="rate-hint">
                                  @ {formatCurrency(normalRate)}
                                </div>
                              </div>
                            ) : (
                              "–"
                            )}
                          </td>
                          <td className="text-right">
                            {hrs2 > 0 ? (
                              <div>
                                {hrs2}
                                <div data-seq-private="" className="rate-hint">
                                  @ {formatCurrency(overtimeRate)}
                                </div>
                              </div>
                            ) : (
                              "–"
                            )}
                          </td>
                          {isLegacyForm && (
                            <td className="text-right">
                              {sat > 0 ? (
                                <div>
                                  {sat}
                                  <div data-seq-private="" className="rate-hint">
                                    @ {formatCurrency(saturdayRate)}
                                  </div>
                                </div>
                              ) : (
                                "–"
                              )}
                            </td>
                          )}
                          <td data-seq-private="" className="text-right font-semibold">
                            {formatCurrency(rowTotal)}
                          </td>
                          <td>
                            <div className="remarks-cell">
                              {(
                                item as unknown as Record<
                                  string,
                                  string
                                >
                              ).remarks || "–"}
                            </div>
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            )}
          </div>

          {/* SECTION 2b: Saturday Contract Labour */}
          {hasSatContract && (
            <div className="var-section var-section--amber">
              <div className="var-section-header var-section-header--amber">
                <LayoutGrid size={18} /> Saturday Labour (Contract)
              </div>
              {satContractRowData.length > 0 ? (
                /* v29+ row-level detail */
                <table className="var-table">
                  <thead className="thead--amber">
                    <tr>
                      <th>Date</th>
                      <th className="text-right">Workers</th>
                      <th>Start</th>
                      <th>Finish</th>
                      <th className="text-right">OT Hrs</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {satContractRowData.map((row, idx) => (
                      <tr key={idx}>
                        <td className="font-medium">{formatISODate(row.date)}</td>
                        <td className="text-right">{row.num_workers || 0}</td>
                        <td>{formatISOTime(row.start_time)}</td>
                        <td>{formatISOTime(row.finish_time)}</td>
                        <td className="text-right font-semibold">{row.hours_ot || 0}</td>
                        <td className="remarks-cell">{row.description || ""}</td>
                      </tr>
                    ))}
                    <tr className="var-table-total var-table-total--amber">
                      <td colSpan={4}>Contract Saturday Total</td>
                      <td className="text-right">{satContractOT} hrs</td>
                      <td data-seq-private="" className="text-right">{formatCurrency(satContractTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                /* Aggregate fallback for old forms */
                <table className="var-table">
                  <thead className="thead--amber">
                    <tr>
                      <th>Type</th>
                      <th className="text-right">Hours</th>
                      <th className="text-right">Rate</th>
                      <th className="text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {satContractOT > 0 && (
                      <tr>
                        <td className="font-medium">Saturday OT</td>
                        <td className="text-right">{satContractOT}</td>
                        <td className="text-right">
                          <span data-seq-private="" className="rate-hint">
                            @ {formatCurrency(satContractOTRate)}
                          </span>
                        </td>
                        <td data-seq-private="" className="text-right font-semibold">
                          {formatCurrency(satContractOT * satContractOTRate)}
                        </td>
                      </tr>
                    )}
                    <tr className="var-table-total var-table-total--amber">
                      <td colSpan={3}>Contract Saturday Total</td>
                      <td data-seq-private="" className="text-right">
                        {formatCurrency(satContractTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* SECTION 2c: Saturday Non-Contract Labour */}
          {hasSatNonContract && (
            <div className="var-section var-section--teal">
              <div className="var-section-header var-section-header--teal">
                <LayoutGrid size={18} /> Saturday Labour
                (Non-Contract)
              </div>
              {satNonContractRowData.length > 0 ? (
                /* v29+ row-level detail */
                <table className="var-table">
                  <thead className="thead--teal">
                    <tr>
                      <th>Date</th>
                      <th className="text-right">Workers</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {satNonContractRowData.map((row, idx) => (
                      <tr key={idx}>
                        <td className="font-medium">{formatISODate(row.date)}</td>
                        <td className="text-right">{row.num_workers || 0}</td>
                        <td className="remarks-cell">{row.description || ""}</td>
                      </tr>
                    ))}
                    <tr className="var-table-total var-table-total--teal">
                      <td>Non-Contract Saturday Total</td>
                      <td className="text-right">{satNonContract} qty</td>
                      <td data-seq-private="" className="text-right">{formatCurrency(satNCTotal)}</td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                /* Aggregate fallback for old forms */
                <table className="var-table">
                  <thead className="thead--teal">
                    <tr>
                      <th>Type</th>
                      <th className="text-right">Qty / Hours</th>
                      <th className="text-right">Rate</th>
                      <th className="text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="font-medium">
                        Saturday (Non-Contract)
                      </td>
                      <td className="text-right">{satNonContract}</td>
                      <td className="text-right">
                        <span data-seq-private="" className="rate-hint">
                          @ {formatCurrency(saturdayRate)}
                        </span>
                      </td>
                      <td data-seq-private="" className="text-right font-semibold">
                        {formatCurrency(satNCTotal)}
                      </td>
                    </tr>
                    <tr className="var-table-total var-table-total--teal">
                      <td colSpan={3}>Non-Contract Saturday Total</td>
                      <td data-seq-private="" className="text-right">
                        {formatCurrency(satNCTotal)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* SECTION 3: Materials Used */}
          <div className="var-section var-section--indigo">
            <div className="var-section-header var-section-header--indigo">
              <DollarSign size={18} /> Materials Used / Others
            </div>
            {materials.length > 0 ? (
              <table className="var-table">
                <thead className="thead--indigo">
                  <tr>
                    <th>Item Name</th>
                    <th className="text-right">Quantity</th>
                    <th className="text-right">Unit Cost</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {materials.map((m, idx) => (
                    <tr key={idx}>
                      <td className="font-medium">
                        {m.title || "Untitled Material"}
                      </td>
                      <td className="text-right">{m.quantity}</td>
                      <td data-seq-private="" className="text-right">
                        {formatCurrency(m.cost)}
                      </td>
                      <td data-seq-private="" className="text-right font-semibold">
                        {formatCurrency(m.total)}
                      </td>
                    </tr>
                  ))}
                  <tr className="var-table-total var-table-total--indigo">
                    <td colSpan={3} className="text-right">
                      Total Materials/Others
                    </td>
                    <td data-seq-private="" className="text-right">
                      {formatCurrency(totalMaterialCost)}
                    </td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="var-section-empty">
                No materials recorded for this variation.
              </div>
            )}
          </div>

          {/* SECTION 4: Hire Items (v29+ only) */}
          {hireItems.length > 0 && (
            <div className="var-section var-section--rose">
              <div className="var-section-header var-section-header--rose">
                <Wrench size={18} /> Hire Items
              </div>
              <table className="var-table">
                <thead className="thead--rose">
                  <tr>
                    <th>Item</th>
                    <th>Description</th>
                    <th>Start Date</th>
                    <th>End Date</th>
                    <th className="text-right">Weeks</th>
                    <th className="text-right">Levels</th>
                    <th className="text-right">Qty</th>
                    <th className="text-right">Cost/Wk</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {hireItems.map((hi, idx) => {
                    const isOpenEnded = !hi.end_date;
                    const displayWeeks = isOpenEnded
                      ? getRunningWeeks(hi.start_date)
                      : (hi.weeks || 0);
                    const displayTotal = getHireItemTotal(hi);
                    return (
                    <tr key={idx}>
                      <td className="font-medium">{hi.item || "—"}</td>
                      <td style={{ maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {hi.item_description || "—"}
                      </td>
                      <td>{formatISODate(hi.start_date)}</td>
                      <td>
                        {hi.end_date
                          ? formatISODate(hi.end_date)
                          : <span className="var-badge var-badge--pending" style={{ fontSize: "10px" }}>Open-ended</span>
                        }
                      </td>
                      <td className="text-right">
                        {isOpenEnded ? (
                          <span title={`${displayWeeks.toFixed(2)} weeks since ${hi.start_date}`} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            {displayWeeks.toFixed(1)}
                            <span style={{ fontSize: "9px", color: "var(--text-muted)", fontWeight: 500 }}>wks</span>
                          </span>
                        ) : (
                          displayWeeks
                        )}
                      </td>
                      <td className="text-right">{hi.levels || 0}</td>
                      <td className="text-right">{hi.qty || 0}</td>
                      <td data-seq-private="" className="text-right">{formatCurrency(hi.cost || hi.manual_cost || 0)}</td>
                      <td data-seq-private="" className="text-right font-semibold">
                        {isOpenEnded ? (
                          <span data-seq-private="" style={{ color: "#f59e0b" }} title="Running total based on weeks to date">
                            {formatCurrency(displayTotal)}
                          </span>
                        ) : (
                          formatCurrency(displayTotal)
                        )}
                      </td>
                    </tr>
                    );
                  })}
                  <tr className="var-table-total var-table-total--rose">
                    <td colSpan={8} className="text-right">Total Hire Items</td>
                    <td data-seq-private="" className="text-right">{formatCurrency(hireItemsTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Grand Total Bar */}
          <div className="var-grand-total">
            <div className="var-grand-total-label">Grand Total</div>
            <div data-seq-private="" className="var-grand-total-value">
              {formatCurrency(groupedVariation.totalAmount)}
            </div>
          </div>
        {/* ─── BOTTOM SECTIONS (inside scrollable body) ─── */}

        {/* Notes */}
        {firstItem?.notes && (
          <div className="var-content-section">
            <div className="var-content-label">
              <FileText size={14} /> Docket Notes
            </div>
            <div className="var-content-block">
              {firstItem.notes || "No notes provided."}
            </div>
          </div>
        )}

        {/* Builder Comments */}
        {firstItem?.builder_comments && (
          <div className="var-content-section">
            <div className="var-content-label">
              <MessageSquare size={14} /> Builder Comments
            </div>
            <div className="var-content-block">
              {builderCommentsText}
            </div>
          </div>
        )}

        {/* Signatures */}
        {(firstItem?.builder_signed ||
          firstItem?.seq_signed ||
          firstItem?.admin_signed) && (
          <div className="var-content-section">
            <div className="var-content-label">
              <PenLine size={14} /> Signatures
              <span className="var-badge var-badge--discount" style={{ fontSize: "0.65rem", padding: "1px 6px" }}>
                Dashpivot
              </span>
            </div>
            <div className="var-signatures">
              {[
                {
                  label: "Builder",
                  signed: firstItem?.builder_signed,
                },
                {
                  label: "Acme Construction",
                  signed: firstItem?.seq_signed,
                },
                {
                  label: "Admin",
                  signed: firstItem?.admin_signed,
                },
              ].map((sig) => (
                <div
                  key={sig.label}
                  className={`var-sig-chip ${sig.signed ? "var-sig-chip--signed" : "var-sig-chip--unsigned"}`}
                >
                  {sig.signed ? (
                    <CheckCircle2 size={16} />
                  ) : (
                    <X size={16} style={{ opacity: 0.5 }} />
                  )}
                  {sig.label}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photos */}
        {groupPhotos.length > 0 && (
          <div className="var-content-section">
            <div className="var-content-label">
              <LayoutGrid size={16} /> Photos ({groupPhotos.length})
            </div>
            <div className="var-photo-grid">
              { }
              {groupPhotos.map(
                (
                  photo: Record<string, Record<string, string>>,
                  idx: number,
                ) => {
                  const thumb =
                    photo.url?.thumbnail ||
                    photo.url?.compressedFile ||
                    photo.url?.originalFile;
                  const full =
                    photo.url?.originalFile ||
                    photo.url?.compressedFile;
                  return (
                    <div
                      key={idx}
                      className="var-photo-thumb"
                      onClick={() =>
                        full && setSelectedImage(full)
                      }
                    >
                      {thumb && (
                        <img
                          src={thumb}
                          alt={`Photo ${idx + 1}`}
                        />
                      )}
                    </div>
                  );
                },
              )}
            </div>
          </div>
        )}
        </div>{/* end var-detail-body */}
      </div>

      {/* Lightbox Overlay */}
      {selectedImage && (
        <div
          className="modal-overlay"
          style={{ zIndex: 1100, background: "rgba(0,0,0,0.9)" }}
          onClick={() => setSelectedImage(null)}
        >
          <div
            style={{
              position: "relative",
              maxWidth: "95vw",
              maxHeight: "95vh",
            }}
          >
            <button
              onClick={() => setSelectedImage(null)}
              style={{
                position: "absolute",
                top: -40,
                right: 0,
                background: "none",
                border: "none",
                color: "white",
                cursor: "pointer",
              }}
            >
              <X size={32} />
            </button>
            <img
              src={selectedImage}
              alt="Full View"
              style={{
                maxWidth: "100%",
                maxHeight: "90vh",
                borderRadius: "4px",
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// --- Main Variants Tab ---

function VariationsTab({
  project,
  variations,
  onVariationsUpdate,
  syncStatus,
  onSyncStart,
  openVariationId,
  openVariationDetailId,
  onClearOpenVariation,
  onClearOpenVariationDetail,
  unreadCounts,
  onMarkRead,
  hireUnreadCounts,
  hireAuditFlags,
  onMarkHireRead,
  onHireFlagChange,
  openHireItemId,
  hireItemLabels,
  onClearOpenHireItem,
}: {
  project: ProjectWithDetails;
  variations: ClaimsVariation[];
  onVariationsUpdate: (variations: ClaimsVariation[]) => void;
  syncStatus: 'idle' | 'syncing' | 'error';
  onSyncStart: () => void;
  openVariationId?: string | null;
  openVariationDetailId?: string | null;
  onClearOpenVariation?: () => void;
  onClearOpenVariationDetail?: () => void;
  unreadCounts?: Record<string, number>;
  onMarkRead?: (variationId: string) => void;
  hireUnreadCounts?: Record<string, number>;
  hireAuditFlags?: Record<string, CommentFlag>;
  onMarkHireRead?: (hireItemId: string) => void;
  onHireFlagChange?: (hireItemId: string, flag: CommentFlag) => void;
  openHireItemId?: string | null;
  hireItemLabels?: Record<string, string>;
  onClearOpenHireItem?: () => void;
}) {
  const { user } = useAuth();
  const tour = useTourManager('project_detail');
  const syncing = syncStatus === 'syncing';
  const [selectedGroup, setSelectedGroup] = useState<GroupedVariation | null>(
    null,
  );
  const [commentsTarget, setCommentsTarget] = useState<{
    entityType: CommentEntityType;
    entityId: string;
    label: string;
  } | null>(null);
  const { addToast } = useToast();

  // SEQ-93: Change reconciliation status for a variation group
  const handleReconciliationChange = useCallback(async (group: GroupedVariation, newStatus: ReconciliationStatus) => {
    const itemIds = group.items.map(item => item.id);
    const { error } = await supabase
      .from('claims_variations')
      .update({
        reconciliation_status: newStatus,
        reconciled_at: new Date().toISOString(),
        reconciled_by: user?.id || null,
      })
      .in('id', itemIds);
    if (error) {
      addToast({ type: 'error', title: 'Failed to update reconciliation status' });
      return;
    }
    // Update local state
    const updatedVariations = variations.map(v =>
      itemIds.includes(v.id) ? { ...v, reconciliation_status: newStatus, reconciled_at: new Date().toISOString(), reconciled_by: user?.id || null } : v
    );
    onVariationsUpdate(updatedVariations);
    const opt = getReconciliationOption(newStatus);
    addToast({
      type: 'success',
      title: `Status: ${opt.label}`,
      message: group.docket_no,
    });
    // Activity log
    if (user) {
      logActivity({
        projectId: project.id,
        userId: user.id,
        actionType: 'UPDATE',
        entityType: 'VARIATION',
        entityId: group.items[0]?.id || group.id,
        description: `Changed reconciliation status to "${opt.label}" for docket ${group.docket_no}`,
        metadata: {
          docket_no: group.docket_no,
          newStatus: newStatus,
          itemCount: itemIds.length,
        },
      });
    }
  }, [variations, onVariationsUpdate, addToast, user, project.id]);

  // ─── Sub-tab state: variations vs install_hire vs combined ───
  type VariationSubTab = 'variations' | 'install_hire';
  const [subTab, setSubTab] = useState<VariationSubTab>('variations');
  const [subTabLoaded, setSubTabLoaded] = useState(false);

  // Load saved preference
  useEffect(() => {
    if (!user?.id || subTabLoaded) return;
    supabase
      .from('profiles')
      .select('variation_view_mode')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data?.variation_view_mode && data.variation_view_mode !== 'combined') {
          setSubTab(data.variation_view_mode as VariationSubTab);
        }
        setSubTabLoaded(true);
      });
  }, [user?.id, subTabLoaded]);

  // Save preference on change
  const handleSubTabChange = useCallback((tab: VariationSubTab) => {
    setSubTab(tab);
    if (user?.id) {
      supabase
        .from('profiles')
        .update({ variation_view_mode: tab })
        .eq('id', user.id)
        .then(({ error }) => {
          if (error) logWarn('Failed to save view preference:', error);
        });
    }
  }, [user?.id]);

  // View mode state
  type VariationViewMode = 'status' | 'docket' | 'date' | 'variance';
  const [viewMode, setViewMode] = useState<VariationViewMode>('status');

  // Filter state
  const [filterDateMode, setFilterDateMode] = useState<"single" | "range">(
    "single",
  );
  const [filterDateStart, setFilterDateStart] = useState("");
  const [filterDateEnd, setFilterDateEnd] = useState("");
  const [filterDocket, setFilterDocket] = useState("");
  // Filter type removed - now just all vs credit (simplified)
  const [showDocketDropdown, setShowDocketDropdown] = useState(false);

  // Status filter: all | valid | invalid | pending
  type StatusFilter = 'all' | 'valid' | 'invalid' | 'pending';
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // SEQ-93: Reconciliation filter
  type ReconFilter = 'all' | 'not_reconciled' | 'reconciled' | 'held' | 'paid';
  const [reconFilter, setReconFilter] = useState<ReconFilter>('all');

  // SEQ-93: Bulk selection state
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkMode, setBulkMode] = useState(false);

  // Status counts (computed from ALL variations, ignoring other filters)
  const statusCounts = useMemo(() => {
    const counts = { valid: 0, invalid: 0, pending: 0, validTotal: 0 };
    variations.forEach((v) => {
      const s = (v.status || 'valid').toLowerCase();
      if (s === 'valid' || !v.status) {
        counts.valid++;
        const rawTotal = Number(v.total) || 0;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const discount = Number((v as any).discount_percent) || 0;
        const effectiveTotal = rawTotal * (1 - discount / 100);
        const multiplier = v.debit_credit === 'credit' ? -1 : 1;
        counts.validTotal += effectiveTotal * multiplier;
      } else if (s === 'invalid') {
        counts.invalid++;
      } else {
        counts.pending++;
      }
    });
    return counts;
  }, [variations]);

  // Get unique docket numbers for autocomplete
  const uniqueDockets = useMemo(() => {
    const dockets = new Set<string>();
    variations.forEach((v) => {
      if (v.docket_no) dockets.add(v.docket_no);
    });
    return Array.from(dockets).sort();
  }, [variations]);

  // Filter docket suggestions based on input
  const filteredDocketSuggestions = useMemo(() => {
    if (!filterDocket) return uniqueDockets.slice(0, 10);
    return uniqueDockets
      .filter((d) => d.toLowerCase().includes(filterDocket.toLowerCase()))
      .slice(0, 10);
  }, [filterDocket, uniqueDockets]);

  const groupedVariations = useMemo<GroupedVariation[]>(() => {
    if (!variations) return [];

    // Apply filters to variations
    let filtered = variations;

    // Date filter
    if (filterDateStart) {
      const startDate = new Date(filterDateStart);
      if (filterDateMode === "single") {
        // Single date: match exact date
        filtered = filtered.filter((v) => {
          const vDate = new Date(v.variation_date);
          return vDate.toDateString() === startDate.toDateString();
        });
      } else if (filterDateEnd) {
        // Range: between start and end
        const endDate = new Date(filterDateEnd);
        filtered = filtered.filter((v) => {
          const vDate = new Date(v.variation_date);
          return vDate >= startDate && vDate <= endDate;
        });
      }
    }

    // Docket filter
    if (filterDocket) {
      filtered = filtered.filter((v) =>
        (v.docket_no || "").toLowerCase().includes(filterDocket.toLowerCase()),
      );
    }

    // Status filter (all | valid | invalid | pending)
    if (statusFilter !== 'all') {
      filtered = filtered.filter((v) => {
        const s = (v.status || 'valid').toLowerCase();
        return s === statusFilter;
      });
    }

    // SEQ-93: Reconciliation filter
    if (reconFilter !== 'all') {
      filtered = filtered.filter((v) => {
        const rs = v.reconciliation_status || null;
        if (reconFilter === 'not_reconciled') return rs === null;
        return rs === reconFilter;
      });
    }

    return buildVariationGroups(filtered.filter((v) => !isHireOnlyVariation(v)));
  }, [
    variations,
    filterDateMode,
    filterDateStart,
    filterDateEnd,
    filterDocket,
    statusFilter,
    reconFilter,
  ]);

  // View-mode sorting: applies on top of the filtered+grouped data
  const sortedVariations = useMemo<GroupedVariation[]>(() => {
    const list = [...groupedVariations];
    switch (viewMode) {
      case 'docket': {
        // Extract numeric suffix from docket_no (e.g. "SEQ-...-49" → 49)
        const extractSuffix = (d: string): number => {
          const parts = d.split('-');
          const last = parts[parts.length - 1];
          const num = parseInt(last, 10);
          return isNaN(num) ? 0 : num;
        };
        return list.sort((a, b) => extractSuffix(a.docket_no) - extractSuffix(b.docket_no));
      }
      case 'date':
        return list.sort(
          (a, b) => new Date(b.variation_date).getTime() - new Date(a.variation_date).getTime(),
        );
      case 'variance':
        return list.sort((a, b) => Math.abs(b.totalAmount) - Math.abs(a.totalAmount));
      case 'status':
      default:
        // Default: date desc (already sorted in groupedVariations)
        return list;
    }
  }, [groupedVariations, viewMode]);

  // For status mode: split into awaiting vs paid
  const awaitingVariations = useMemo(
    () => sortedVariations.filter((g) => g.totalWaiting > 0.01),
    [sortedVariations],
  );
  const paidVariations = useMemo(
    () => sortedVariations.filter((g) => g.totalWaiting <= 0.01),
    [sortedVariations],
  );


  // Handle auto-opening of the EntityCommentsModal (from notification deep links)
  useEffect(() => {
    if (openVariationId && groupedVariations.length > 0) {
      const targetGroup = groupedVariations.find(
        (g) =>
          g.id === openVariationId ||
          g.items.some((i) => i.id === openVariationId),
      );
      if (targetGroup) {
        setCommentsTarget({
          entityType: "variation",
          entityId: openVariationId,
          label: targetGroup.docket_no,
        });
        onClearOpenVariation?.();
      }
    }
  }, [openVariationId, groupedVariations, onClearOpenVariation]);

  // Handle auto-opening of the hire audit notes (from notification deep links).
  // Gated on subTabLoaded so the saved sub-tab preference, which resolves
  // asynchronously and would otherwise land second, cannot switch the view back
  // out from under the deep link.
  useEffect(() => {
    if (!openHireItemId || !subTabLoaded) return;
    setSubTab('install_hire');
    setCommentsTarget({
      entityType: "hire_item",
      entityId: openHireItemId,
      label: hireItemLabels?.[openHireItemId] || "Hire item",
    });
    onClearOpenHireItem?.();
  }, [openHireItemId, subTabLoaded, hireItemLabels, onClearOpenHireItem]);

  // Handle auto-opening of the VariationDetailModal (from notes mention deep links)
  useEffect(() => {
    if (openVariationDetailId && groupedVariations.length > 0) {
      const targetGroup = groupedVariations.find(
        (g) =>
          g.id === openVariationDetailId ||
          g.items.some((i) => i.id === openVariationDetailId),
      );
      if (targetGroup) {
        setSelectedGroup(targetGroup);
        onClearOpenVariationDetail?.();
      }
    }
  }, [openVariationDetailId, groupedVariations, onClearOpenVariationDetail]);

  // CSV Import State
  const [importing, setImporting] = useState(false);
   
  const [_isImportModalOpen, setIsImportModalOpen] = useState(false);
   
   
  const [_isAddMenuOpen, _setIsAddMenuOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [importProgress, setImportProgress] = useState({
    processed: 0,
    total: 0,
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
  const [validationResult, setValidationResult] = useState<any>(null);
  // Per-row import status: { [rowIndex]: 'pending' | 'importing' | 'success' | 'failed' }
  const [importStatus, setImportStatus] = useState<
    Record<number, "pending" | "importing" | "success" | "failed">
  >({});
  const [importRowErrors, setImportRowErrors] = useState<
    Record<number, string>
  >({});
  const [importResults, setImportResults] = useState<{
    successCount: number;
    failCount: number;
    errors: string[];
  }>({ successCount: 0, failCount: 0, errors: [] });
  // Add Date Format State
  const [dateFormat, setDateFormat] = useState<"DMY" | "MDY">("DMY");
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
  const [rawRows, setRawRows] = useState<any[]>([]);

  // Re-validate when dateFormat changes
  useEffect(() => {
    if (rawRows.length > 0 && project) {
      // Build lookup
      const existingVariations = variations.map((v) => ({
        dl1: v.hours_lvl_1 || 0,
        dl2: v.hours_lvl_2 || 0,
        material: v.material_cost || 0,
        total: v.total || 0,
      }));

      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
      const result = (validateVariationsCSV as any)(
        rawRows,
        project.normal_rate || 0,
        project.overtime_rate || 0,
        project.saturday_rate || 0,
        existingVariations,
        dateFormat,
      );
      setValidationResult(result);
    }
  }, [dateFormat, project, rawRows, variations]);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment -- auto-suppressed for safety
  // @ts-ignore - Dead code: CSV import feature was removed from UI but handler code remains
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- auto-suppressed for safety
  const handleFileImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
        const rows = (parseVariationsCSV as any)(text);
        setRawRows(rows); // Store mainly for re-validation on format change

        // Build existing variations lookup for duplicate detection by values
        const existingVariations = variations.map((v) => ({
          dl1: v.hours_lvl_1 || 0,
          dl2: v.hours_lvl_2 || 0,
          material: v.material_cost || 0,
          total: v.total || 0,
        }));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
        const result = (validateVariationsCSV as any)(
          rows,
          project.normal_rate || 0,
          project.overtime_rate || 0,
          project.saturday_rate || 0,
          existingVariations,
          dateFormat,
        );

        setValidationResult(result);
        setShowPreview(true);
        setIsImportModalOpen(true);
      } catch (error) {
        logError("CSV Parse Error:", error);
        addToast({
          title: "Error parsing CSV",
          message: "Please check the file format and try again",
          type: "error",
        });
      }
    };
    reader.readAsText(file);
  };

  const handleVariationChange = (
    index: number,
    field: string,
    value: string | number,
  ) => {
    if (!validationResult || !project) return;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    setValidationResult((prev: any) => {
      if (!prev) return null;
      const newValid = [...prev.valid];
      const row = { ...newValid[index], [field]: value };

      // Recalculate totals if necessary
      if (
        field === "hours_lvl_1" ||
        field === "hours_lvl_2" ||
        field === "saturday_work_qty" ||
        field === "sat_contract_ot_qty" ||
        field === "material_cost" ||
        field === "approved_amount"
      ) {
        const normalRate = project.normal_rate || 0;
        const overtimeRate = project.overtime_rate || 0;
        const saturdayRate = project.saturday_rate || 400;
        const satContractOTRate = project.saturday_contract_ot_rate || project.overtime_rate || 0;

        row.total =
          (typeof row.hours_lvl_1 === "number" ? row.hours_lvl_1 : 0) *
          normalRate +
          (typeof row.hours_lvl_2 === "number" ? row.hours_lvl_2 : 0) *
          overtimeRate +
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (typeof (row as any).sat_contract_ot_qty === "number"
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ? (row as any).sat_contract_ot_qty
            : 0) *
          satContractOTRate +
          (typeof row.saturday_work_qty === "number"
            ? row.saturday_work_qty
            : 0) *
          saturdayRate +
          (typeof row.material_cost === "number" ? row.material_cost : 0);

        row.waiting_amount = Math.max(
          0,
          row.total -
          (typeof row.approved_amount === "number" ? row.approved_amount : 0),
        );
      }

      newValid[index] = row;

      // Recalculate summary
      const summary = { ...prev.summary };
      // summary.totalVariations = newValid.length; // Count doesn't change on edit

      summary.totalHoursLvl1 = newValid.reduce(
        (sum, v) => sum + v.hours_lvl_1,
        0,
      );
      summary.totalHoursLvl2 = newValid.reduce(
        (sum, v) => sum + v.hours_lvl_2,
        0,
      );
      summary.totalMaterialCost = newValid.reduce(
        (sum, v) => sum + v.material_cost,
        0,
      );
      summary.totalApproved = newValid.reduce(
        (sum, v) => sum + v.approved_amount,
        0,
      );
      summary.totalWaiting = newValid.reduce(
        (sum, v) => sum + v.waiting_amount,
        0,
      );
      summary.grandTotal = newValid.reduce((sum, v) => sum + v.total, 0);

      return { ...prev, valid: newValid, summary };
    });
  };

  const handleBulkImport = async (indicesToProcess?: number[]) => {
    if (!validationResult || validationResult.valid.length === 0) return;

    setImporting(true);

    // Compute indices to import
    let allIndices: number[] = [];
    if (indicesToProcess && Array.isArray(indicesToProcess)) {
      allIndices = indicesToProcess;
    } else {
      allIndices = validationResult.valid
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
        .map((v: any, i: number) => (v.status === "valid" ? i : -1))
        .filter((i: number): i is number => i !== -1);
    }
    // Batch size reduced to 5 to specific timeouts (Edge Function takes ~2s/item)
    const BATCH_SIZE = 5;

    if (allIndices.length === 0) {
      addToast({
        title: "Nothing to import",
        message:
          "No valid rows found. Please fix errors or mark duplicates as valid.",
        type: "info",
      });
      setImporting(false);
      return;
    }

    // Initialize status for new items only
    setImportStatus((prev) => {
      const next = { ...prev };
      allIndices.forEach((i) => {
        next[i] = "pending";
      });
      return next;
    });

    // Initialize progress
    setImportProgress({ processed: 0, total: allIndices.length });

    // Reset results if full import
    if (!indicesToProcess) {
      setImportResults({ successCount: 0, failCount: 0, errors: [] });
      setImportRowErrors({});
    }

    try {
      // Helper to process a batch
      // Helper to process a batch with retry
      const processBatch = async (
        batchIndices: number[],
        retryCount = 0,
        maxRetries = 3,
      ): Promise<void> => {
        const batchVariations = batchIndices.map(
          (idx) => validationResult.valid[idx],
        );

        // Update status to importing
        setImportStatus((prev) => {
          const next = { ...prev };
          batchIndices.forEach((idx) => {
            next[idx] = "importing";
          });
          return next;
        });

        const { data, error } = await supabase.functions.invoke(
          "bulk-import-variations",
          {
            body: {
              projectId: project.id,
              variations: batchVariations,
            },
          },
        );

        // Handle specific network errors or timeouts with retry
        if (error || !data) {
          logError(`Batch error (Attempt ${retryCount + 1}/${maxRetries}):`, error);

          if (retryCount < maxRetries) {
            // Wait 1s and retry
            await new Promise((resolve) => setTimeout(resolve, 1000));
            return processBatch(batchIndices, retryCount + 1, maxRetries);
          }

          // Max retries reached - fail the batch
          setImportStatus((prev) => {
            const next = { ...prev };
            batchIndices.forEach((idx) => {
              next[idx] = "failed";
            });
            return next;
          });
          const batchErrors: Record<number, string> = {};
          batchIndices.forEach((idx) => {
            batchErrors[idx] =
              error?.message || "Batch failed after 3 attempts";
          });
          setImportRowErrors((prev) => ({ ...prev, ...batchErrors }));

          setImportResults((prev) => ({
            ...prev,
            failCount: prev.failCount + batchIndices.length,
            errors: [
              ...prev.errors,
              `Batch failed (3 attempts): ${error?.message || "Unknown error"}`,
            ],
          }));
          return;
        }

        if (data && data.results) {
          // CHECK FOR ORPHANED ROWS (Silent backend failures)
          if (data.results.length < batchIndices.length) {
            const missingCount = batchIndices.length - data.results.length;
            console.error(
              `Batch mismatch: Sent ${batchIndices.length}, got ${data.results.length}. Marking ${missingCount} as failed.`,
            );

            // Pad results with failures for missing items
            for (let k = 0; k < missingCount; k++) {
              data.results.push({
                success: false,
                error: "Timeout or backend crash: No result returned",
              });
            }
          }

          // Map results back to original indices
          const newStatusUpdate: Record<number, "success" | "failed"> = {};
          const newRowErrors: Record<number, string> = {};
          let batchSuccess = 0;
          let batchFail = 0;
          const newErrors: string[] = [];

          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
          data.results.forEach((res: any, indexInBatch: number) => {
            // Safety check for index mismatch
            if (indexInBatch >= batchIndices.length) return;

            const originalIndex = batchIndices[indexInBatch];
            if (res.success) {
              newStatusUpdate[originalIndex] = "success";
              batchSuccess++;
            } else {
              newStatusUpdate[originalIndex] = "failed";
              batchFail++;
              const errMsg = res.error || "Unknown error";
              newErrors.push(`Row ${originalIndex + 1}: ${errMsg}`);
              newRowErrors[originalIndex] = errMsg;
            }
          });

          setImportStatus((prev) => ({ ...prev, ...newStatusUpdate }));
          setImportRowErrors((prev) => ({ ...prev, ...newRowErrors }));
          setImportResults((prev) => ({
            successCount: prev.successCount + batchSuccess,
            failCount: prev.failCount + batchFail,
            errors: [...prev.errors, ...newErrors],
          }));
        }
      };

      // Process in batches
      for (let i = 0; i < allIndices.length; i += BATCH_SIZE) {
        const batchIndices = allIndices.slice(i, i + BATCH_SIZE);
        await processBatch(batchIndices);

        // Update progress
        setImportProgress((prev) => ({
          ...prev,
          processed: Math.min(prev.processed + batchIndices.length, prev.total),
        }));
      }

      // Final success check is implicit in UI results
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    } catch (err: any) {
      logError("Global import error:", err);
      // Ensure we don't hang in importing state
    } finally {
      // Show completion toast
      setImporting(false);

      // Get final counts from state (use callback to get latest)
      setTimeout(() => {
        // Calculate final stats dynamically from state or accumulator
        setImportResults((final) => {
          if (final.failCount > 0) {
            addToast({
              title: "Import Finished with Errors",
              message: `${final.successCount} succeeded, ${final.failCount} failed. Check red rows above.`,
              type: "warning", // Orange warning for partial failure
            });
          } else {
            addToast({
              title: "Import Complete",
              message: `All ${final.successCount} items imported successfully.`,
              type: "success",
            });
          }
          return final;
        });
      }, 500);
    }
  };

  // Retry failed imports only
  const handleRetryFailed = async () => {
    const failedIndices = Object.entries(importStatus)
       
      .filter(([_, status]) => status === "failed")
      .map(([index]) => parseInt(index, 10))
      .sort((a, b) => a - b);

    if (failedIndices.length > 0) {
      // Keep existing validationResult, just re-run for these indices
      await handleBulkImport(failedIndices);
    } else {
      addToast({ title: "No failed rows to retry", type: "info" });
    }
  };

  const handleSync = async () => {
    if (!project?.id || syncing) return;

    // Optimistically set parent syncStatus + start polling immediately
    onSyncStart();

    addToast({
      type: "info",
      title: "Syncing Variations",
      message: "Syncing variations from Dashpivot...",
      duration: 3000,
    });

    // Fire-and-forget: the parent's polling detects sync_status change in DB.
    supabase.functions.invoke(
      "sync-variations",
      { body: { projectId: project.id } },
    ).then(({ data, error }) => {
      if (error) {
        const isRateLimit = /429|rate.?limit|too many/i.test(error.message || "");
        addToast({
          type: isRateLimit ? "warning" : "info",
          title: isRateLimit ? "Dashpivot Rate Limited" : "Sync Running in Background",
          message: isRateLimit
            ? "Dashpivot is temporarily limiting requests. Please try again in a few minutes."
            : "The sync is taking longer than usual and will complete in the background.",
          duration: isRateLimit ? undefined : 6000,
        });
        return;
      }

      // Handle already_syncing response from edge function lock
      if (data?.status === 'already_syncing') {
        addToast({
          type: "info",
          title: "Sync In Progress",
          message: data.message || "A sync is already in progress. Please wait.",
          duration: 5000,
        });
        return;
      }

      // Handle atomic failure: existing data preserved
      if (data?.success === false && data?.atomic) {
        addToast({
          type: "error",
          title: "Sync Failed",
          message: data.message || "Sync failed. Your existing data was preserved. Please retry.",
          duration: 8000,
        });
        return;
      }

      // Removal guards report only through results[]. Surface them, or a
      // suppressed removal check and a growing flagged backlog stay invisible.
      for (const notice of filterNewNotices(extractRemovalNotices(data?.results))) {
        addToast({
          type: notice.kind === "skipped" ? "warning" : "info",
          title: notice.title,
          message: notice.message,
          // A suppressed removal check is actionable now; a backlog is a
          // standing condition, so it gets the shorter, quieter treatment.
          duration: notice.kind === "skipped" ? 12000 : 6000,
        });
      }

      // Edge function completed, parent polling will refresh UI.
    }).catch((err) => {
      logError("Sync failed:", err);
      // DON'T show error, sync may still be running in the background.
      addToast({
        type: "info",
        title: "Sync Running in Background",
        message: "Connection interrupted but the sync may still be running. Waiting for completion...",
        duration: 6000,
      });
    });
  };

  const handleApprovalChange = async (
    variationId: string,
    approvedAmount: number,
    total: number,
  ): Promise<void> => {
    const waitingAmount = Math.max(0, total - approvedAmount);

    // 1. Update local DB
    const { error } = await supabase
      .from("claims_variations")
      .update({
        approved_amount: approvedAmount,
        waiting_amount: waitingAmount,
      })
      .eq("id", variationId);

    if (error) throw error;

    // 2. Update local state
    onVariationsUpdate(
      variations.map((v) =>
        v.id === variationId
          ? {
            ...v,
            approved_amount: approvedAmount,
            waiting_amount: waitingAmount,
          }
          : v,
      ),
    );

    // LOG ACTIVITY
    if (user) {
      const variation = variations.find((v) => v.id === variationId);
      logActivity({
        projectId: project.id,
        userId: user.id,
        actionType: "UPDATE",
        entityType: "VARIATION",
        entityId: variationId,
        description: `Updated approved amount for variation: ${variation?.title || "Unknown"
          }`,
        metadata: {
          oldApproved: variation?.approved_amount,
          newApproved: approvedAmount,
          total: total,
        },
      });
    }

    // 3. Push to Dashpivot (await)
    const variation = variations.find((v) => v.id === variationId);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
    if (variation && (variation as any).dashpivot_form_id) {
      const { error: pushError } = await supabase.functions.invoke(
        "push-variation-update",
        {
          body: {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
            dashpivotFormId: (variation as any).dashpivot_form_id,
            approvedAmount: Math.round(approvedAmount * 100) / 100, // Fix floating point
            projectId: project.id,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
            rowIndex: (variation as any).row_index ?? 0, // Target specific row
          },
        },
      );

      if (pushError) {
        logError("Push to Dashpivot failed:", pushError);
        throw new Error("Dashpivot sync failed");
      }
      // The push response carries the approved amount, so it is not logged.
    }
  };
  // Calculate filtered totals (overall + per-section for status view)
  const filteredTotals = useMemo(() => {
    let total = 0;
    let approved = 0;
    let waiting = 0;
    groupedVariations.forEach((g) => {
      total += g.totalAmount;
      approved += g.totalApproved;
      waiting += g.totalWaiting;
    });

    // Per-section totals for status view
    const calcSection = (arr: GroupedVariation[]) => {
      let t = 0, a = 0, w = 0;
      arr.forEach((g) => { t += g.totalAmount; a += g.totalApproved; w += g.totalWaiting; });
      return { total: t, approved: a, waiting: w, count: arr.length };
    };

    return {
      total, approved, waiting, count: groupedVariations.length,
      awaiting: calcSection(awaitingVariations),
      paid: calcSection(paidVariations),
    };
  }, [groupedVariations, awaitingVariations, paidVariations]);

  const hasActiveFilters = filterDateStart || filterDocket || statusFilter !== 'all' || reconFilter !== 'all';

  return (
    <div>
      {/* ─── Sub-Tab Toggle: Variations / Install & Hire ─── */}
      <div className="var-subtab-toggle" data-tour-id="var-subtab-toggle">
        {[
          { key: 'variations' as VariationSubTab, label: 'Variations', icon: <FileText size={16} /> },
          { key: 'install_hire' as VariationSubTab, label: 'Install & Hire', icon: <Wrench size={16} /> },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            className={`var-subtab-btn${subTab === t.key ? ' var-subtab-btn--active' : ''}`}
            onClick={() => handleSubTabChange(t.key)}
            {...(t.key === 'install_hire' ? { 'data-tour-id': 'var-install-hire-tab' } : {})}
          >
            <span className="var-subtab-icon">{t.icon}</span>
            {t.label}
            {t.key === 'install_hire' && (
              <span style={{
                fontSize: '0.55rem',
                fontWeight: 700,
                letterSpacing: '0.06em',
                padding: '1px 5px',
                borderRadius: '4px',
                background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                color: '#fff',
                marginLeft: 6,
                textTransform: 'uppercase',
                lineHeight: 1.4,
              }}>
                BETA
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ─── Shared Action Bar (all sub-tabs) ─── */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "var(--spacing-md)",
          marginBottom: "var(--spacing-md)",
        }}
      >
        <button
          className="btn btn-secondary"
          onClick={handleSync}
          disabled={syncing}
        >
          {syncing ? (
            <img
              src="/SEQ_logo_only.svg"
              alt="Syncing"
              style={{ width: 16, height: 16 }}
              className="animate-spin"
            />
          ) : (
            <RefreshCw size={16} />
          )}
          {syncing ? " Syncing..." : " Sync Dashpivot"}
        </button>

        <CreateInDashpivotLink />
      </div>

      {/* ─── Install & Hire View ─── */}
      {subTab === 'install_hire' && (
        <HireCommentsProvider
          flags={hireAuditFlags || {}}
          unreadCounts={hireUnreadCounts || {}}
          onOpen={(hireItemId, label) => setCommentsTarget({
            entityType: "hire_item",
            entityId: hireItemId,
            label,
          })}
        >
          <HireRegisterSection
            projectId={project.id}
            syncStatus={syncStatus}
            onVariationClick={(variationId) => {
              // SEQ-226: search the full, unfiltered list so hire-only and
              // filtered-out variations open too; a miss tells the user.
              const targetGroup = findSourceVariationGroup(variations, variationId);
              if (targetGroup) {
                setSelectedGroup(targetGroup);
              } else {
                addToast({
                  type: "warning",
                  title: "Source variation not found",
                  message: "It may have been removed from Dashpivot.",
                });
              }
            }}
          />
        </HireCommentsProvider>
      )}

      {/* ─── Variations View (standard list) ─── */}
      {subTab === 'variations' && (
      <>
      {/* ─── Status Summary Cards ─── */}
      <div className="var-status-cards" data-tour-id="var-status-cards">
        <button
          type="button"
          className={`var-status-card var-status-card--valid${statusFilter === 'valid' ? ' var-status-card--active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'valid' ? 'all' : 'valid')}
        >
          <div className="var-status-card-icon">
            <CheckCircle2 size={18} />
          </div>
          <div className="var-status-card-content">
            <span className="var-status-card-label">Valid</span>
            <span className="var-status-card-value">{statusCounts.valid}</span>
            <span data-seq-private="" className="var-status-card-amount">{formatCurrency(statusCounts.validTotal)}</span>
          </div>
        </button>
        <button
          type="button"
          className={`var-status-card var-status-card--invalid${statusFilter === 'invalid' ? ' var-status-card--active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'invalid' ? 'all' : 'invalid')}
        >
          <div className="var-status-card-icon">
            <XCircle size={18} />
          </div>
          <div className="var-status-card-content">
            <span className="var-status-card-label">Invalid</span>
            <span className="var-status-card-value">{statusCounts.invalid}</span>
          </div>
        </button>
        <button
          type="button"
          className={`var-status-card var-status-card--pending${statusFilter === 'pending' ? ' var-status-card--active' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
        >
          <div className="var-status-card-icon">
            <Clock size={18} />
          </div>
          <div className="var-status-card-content">
            <span className="var-status-card-label">Pending</span>
            <span className="var-status-card-value">{statusCounts.pending}</span>
          </div>
        </button>
      </div>

      {/* Filter Toolbar */}
      <div
        className="card"
        style={{
          marginBottom: "var(--spacing-lg)",
          padding: "8px",
          display: "flex",
          gap: "16px",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* Search Bar - Flex Grow */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: "12px",
            padding: "0 16px",
            minWidth: "240px",
            borderRight: "1px solid var(--border-color)",
          }}
        >
          <Search size={18} className="text-muted" />
          <div style={{ position: "relative", flex: 1 }}>
            <input
              type="text"
              value={filterDocket}
              onChange={(e) => {
                setFilterDocket(e.target.value);
                setShowDocketDropdown(true);
              }}
              onFocus={() => setShowDocketDropdown(true)}
              placeholder="Search by docket number..."
              style={{
                width: "100%",
                border: "none",
                background: "transparent",
                fontSize: "0.95rem",
                outline: "none",
                padding: "8px 0",
              }}
            />
            {showDocketDropdown && filteredDocketSuggestions.length > 0 && (
              <>
                <div
                  style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 40,
                  }}
                  onClick={() => setShowDocketDropdown(false)}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: -16,
                    marginTop: 8,
                    background: "var(--bg-card)",
                    border: "1px solid var(--border-color)",
                    borderRadius: "var(--border-radius-md)",
                    boxShadow: "var(--shadow-lg)",
                    zIndex: 50,
                    width: "calc(100% + 32px)",
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  {filteredDocketSuggestions.map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => {
                        setFilterDocket(d);
                        setShowDocketDropdown(false);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        padding: "8px 16px",
                        textAlign: "left",
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        fontSize: "0.875rem",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "var(--bg-hover)")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "none")
                      }
                    >
                      {d}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right Side Controls */}
        <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
          {/* Date Filter */}
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                background: "var(--bg-tertiary)",
                borderRadius: "8px",
                padding: "4px",
              }}
            >
              <button
                type="button"
                onClick={() => setFilterDateMode("single")}
                style={{
                  padding: "6px 12px",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                  background:
                    filterDateMode === "single" ? "white" : "transparent",
                  color: "var(--text-secondary)",
                  boxShadow:
                    filterDateMode === "single"
                      ? "0 1px 2px rgba(0,0,0,0.1)"
                      : "none",
                }}
              >
                Single
              </button>
              <button
                type="button"
                onClick={() => setFilterDateMode("range")}
                style={{
                  padding: "6px 12px",
                  fontSize: "0.85rem",
                  fontWeight: 500,
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                  background:
                    filterDateMode === "range" ? "white" : "transparent",
                  color: "var(--text-secondary)",
                  boxShadow:
                    filterDateMode === "range"
                      ? "0 1px 2px rgba(0,0,0,0.1)"
                      : "none",
                }}
              >
                Range
              </button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                type="date"
                value={filterDateStart}
                onChange={(e) => setFilterDateStart(e.target.value)}
                style={{
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid var(--border-color)",
                  background: "white",
                  fontSize: "0.875rem",
                  color: "var(--text-primary)",
                  outline: "none",
                  width: "135px",
                }}
              />
              {filterDateMode === "range" && (
                <>
                  <span
                    style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}
                  >
                    to
                  </span>
                  <input
                    type="date"
                    value={filterDateEnd}
                    onChange={(e) => setFilterDateEnd(e.target.value)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: "8px",
                      border: "1px solid var(--border-color)",
                      background: "white",
                      fontSize: "0.875rem",
                      color: "var(--text-primary)",
                      outline: "none",
                      width: "135px",
                    }}
                  />
                </>
              )}
            </div>
          </div>

          {/* Clear Button */}
           {hasActiveFilters && (
            <button
              type="button"
              onClick={() => {
                setFilterDateStart("");
                setFilterDateEnd("");
                setFilterDocket("");
                setStatusFilter('all');
                setReconFilter('all');
              }}
              style={{
                width: "32px",
                height: "32px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "50%",
                border: "1px solid var(--border-color)",
                background: "white",
                color: "var(--text-muted)",
                cursor: "pointer",
                transition: "all 0.2s",
              }}
              title="Clear Filters"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>

      {/* ─── Filter Chips: Status + Reconciliation ─── */}
      <div data-tour-id="var-filter-chips" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: 'var(--spacing-md)', flexWrap: 'wrap' }}>
        <Filter size={14} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
        <div className="var-filter-chips">
          {/* Status filter chips */}
          {([
            { key: 'all' as StatusFilter, label: 'All', dotClass: 'var-filter-chip-dot--all' },
            { key: 'valid' as StatusFilter, label: 'Valid', dotClass: 'var-filter-chip-dot--valid' },
            { key: 'invalid' as StatusFilter, label: 'Invalid', dotClass: 'var-filter-chip-dot--invalid' },
            { key: 'pending' as StatusFilter, label: 'Pending', dotClass: 'var-filter-chip-dot--pending' },
          ]).map((chip) => (
            <button
              key={chip.key}
              type="button"
              className={`var-filter-chip${statusFilter === chip.key ? ' var-filter-chip--active' : ''}`}
              onClick={() => setStatusFilter(chip.key)}
            >
              <span className={`var-filter-chip-dot ${chip.dotClass}${chip.key === 'pending' ? ' var-pending-dot' : ''}`} />
              {chip.label}
            </button>
          ))}

          {/* Divider */}
          <span className="recon-filter-divider" />

          {/* Reconciliation filter chips */}
          <span className="recon-filter-label">Reconciliation:</span>
          {([
            { key: 'all' as ReconFilter, label: 'All', dotClass: 'var-filter-chip-dot--all' },
            { key: 'not_reconciled' as ReconFilter, label: 'Not Reconciled', dotClass: 'var-filter-chip-dot--not-recon' },
            { key: 'reconciled' as ReconFilter, label: 'Reconciled', dotClass: 'var-filter-chip-dot--recon' },
            { key: 'held' as ReconFilter, label: 'Held', dotClass: 'var-filter-chip-dot--held' },
            { key: 'paid' as ReconFilter, label: 'Paid', dotClass: 'var-filter-chip-dot--paid-status' },
          ]).map((chip) => (
            <button
              key={`recon-${chip.key}`}
              type="button"
              className={`var-filter-chip${reconFilter === chip.key ? ' var-filter-chip--active' : ''}`}
              onClick={() => setReconFilter(chip.key)}
            >
              <span className={`var-filter-chip-dot ${chip.dotClass}`} />
              {chip.label}
            </button>
          ))}
        </div>

        {/* Bulk mode toggle */}
        <button
          type="button"
          className={`btn btn-sm${bulkMode ? ' btn-primary' : ' btn-secondary'}`}
          onClick={() => { setBulkMode(!bulkMode); setBulkSelected(new Set()); }}
          style={{ marginLeft: 'auto' }}
        >
          {bulkMode ? 'Exit Bulk' : 'Bulk Edit'}
        </button>
      </div>

      {/* ─── Bulk Action Toolbar ─── */}
      {bulkMode && bulkSelected.size > 0 && (
        <div className="card recon-bulk-bar">
          <span className="recon-bulk-count">
            {bulkSelected.size} selected
          </span>
          <span className="recon-bulk-divider" />
          <span className="recon-bulk-label">Set status:</span>
          {RECONCILIATION_OPTIONS.map((opt) => (
            <button
              key={opt.value ?? 'null'}
              type="button"
              className={`recon-bulk-btn recon-bulk-btn--${opt.key}`}
              onClick={async () => {
                const selectedGroups = sortedVariations.filter(g => bulkSelected.has(g.id));
                for (const group of selectedGroups) {
                  await handleReconciliationChange(group, opt.value);
                }
                setBulkSelected(new Set());
              }}
            >
              {opt.label}
            </button>
          ))}
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => setBulkSelected(new Set())}
            style={{ marginLeft: 'auto' }}
          >
            Clear
          </button>
        </div>
      )}

      {/* Filtered Summary - Inline Style */}
      {hasActiveFilters && (
        <div
          style={{
            marginBottom: "var(--spacing-lg)",
            display: "flex",
            gap: "24px",
            alignItems: "center",
            padding: "0 8px",
          }}
        >
          <span
            style={{
              fontSize: "0.875rem",
              fontWeight: 600,
              color: "var(--text-primary)",
            }}
          >
            Found {filteredTotals.count} variation
            {filteredTotals.count !== 1 ? "s" : ""}
          </span>
          <div
            style={{ width: 1, height: 16, background: "var(--border-color)" }}
          />
          <span style={{ fontSize: "0.875rem" }}>
            Total: <b data-seq-private="">{formatCurrency(filteredTotals.total)}</b>
          </span>
          <span style={{ fontSize: "0.875rem", color: "var(--color-success)" }}>
            Approved: <b data-seq-private="">{formatCurrency(filteredTotals.approved)}</b>
          </span>
          <span style={{ fontSize: "0.875rem", color: "var(--color-warning)" }}>
            Waiting: <b data-seq-private="">{formatCurrency(filteredTotals.waiting)}</b>
          </span>
        </div>
      )}

      <div
        style={{
          marginBottom: "var(--spacing-lg)",
        }}
      >
        <h3>Variations</h3>
      </div>

      {/* View Mode Switcher */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "var(--spacing-lg)",
        }}
      >
        <span style={{ fontSize: "0.85rem", color: "var(--text-muted)", fontWeight: 500 }}>
          View:
        </span>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            background: "var(--bg-tertiary)",
            borderRadius: "10px",
            padding: "3px",
            gap: "2px",
          }}
        >
          {([
            { key: 'status' as const, label: 'Awaiting / Paid', icon: <LayoutGrid size={14} /> },
            { key: 'docket' as const, label: 'Docket #', icon: <Hash size={14} /> },
            { key: 'date' as const, label: 'Date', icon: <Calendar size={14} /> },
            { key: 'variance' as const, label: 'Total Variance', icon: <ArrowUpDown size={14} /> },
          ]).map((mode) => (
            <button
              key={mode.key}
              type="button"
              onClick={() => setViewMode(mode.key)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "7px 14px",
                fontSize: "0.825rem",
                fontWeight: viewMode === mode.key ? 600 : 500,
                borderRadius: "8px",
                border: "none",
                cursor: "pointer",
                background: viewMode === mode.key ? "white" : "transparent",
                color: viewMode === mode.key ? "var(--text-primary)" : "var(--text-muted)",
                boxShadow: viewMode === mode.key ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                transition: "all 0.2s",
                whiteSpace: "nowrap",
              }}
            >
              {mode.icon}
              {mode.label}
            </button>
          ))}
        </div>
      </div>

      {/* Grouped Variations Table */}
      {groupedVariations.length === 0 ? (
        <div
          className="card"
          style={{ textAlign: "center", padding: "var(--spacing-2xl)" }}
        >
          <DollarSign
            size={48}
            style={{
              color: "var(--text-muted)",
              marginBottom: "var(--spacing-md)",
            }}
          />
          <p style={{ color: "var(--text-muted)" }}>
            No variations yet. Add your first variation above.
          </p>
        </div>
      ) : (() => {
        // --- Shared row renderer ---
        const getRowStatusClass = (group: GroupedVariation): string => {
          const s = (group.status || 'valid').toLowerCase();
          if (s === 'invalid') return ' variation-row-invalid';
          if (s === 'pending') return ' variation-row-pending';
          return ' variation-row-valid';
        };

        const renderRow = (group: GroupedVariation, index: number, _highlight?: 'awaiting' | 'paid') => (
          <tr
            key={group.id}
            onClick={() => setSelectedGroup(group)}
            style={{
              cursor: "pointer",
              transition: "background 0.2s",
              animation: `rowSlideIn 0.25s cubic-bezier(0.23, 1, 0.32, 1) ${index * 0.03}s both`,
            }}
            className={`hover-row${getRowStatusClass(group)}`}
            {...(index === 0 ? { 'data-tour-id': 'var-first-row' } : {})}
          >
            {bulkMode && (
              <td style={{ width: '36px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={bulkSelected.has(group.id)}
                  onChange={(e) => {
                    const next = new Set(bulkSelected);
                    if (e.target.checked) next.add(group.id);
                    else next.delete(group.id);
                    setBulkSelected(next);
                  }}
                  className="recon-checkbox"
                />
              </td>
            )}
            <td>
              {(() => {
                const [y, m, d] = group.variation_date.split("-");
                const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
                return date.toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
              })()}
            </td>
            <td style={{ fontWeight: 600 }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div title={group.docket_no}>
                  {group.docket_no.length > 20 &&
                    group.docket_no.toUpperCase().startsWith("SEQ") &&
                    group.docket_no.includes("-")
                    ? `SEQ-${group.docket_no.split("-").pop() || group.docket_no}`
                    : group.docket_no}
                </div>
              </div>
            </td>
            <td style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: "0.85rem", color: "var(--text-secondary)" }} title={group.description || ""}>
              {group.description || "—"}
            </td>
            <td>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-start" }}>
                {Array.from(new Set(group.items.map((i) => i.debit_credit))).map((type) => (
                  <span
                    key={type}
                    style={{
                      background: type === "credit" ? "#dbeafe" : "#f3f4f6",
                      color: type === "credit" ? "#1e40af" : "#374151",
                      border: `1px solid ${type === "credit" ? "#93c5fd" : "#d1d5db"}`,
                      padding: "4px 12px", borderRadius: "20px", fontSize: "0.7rem",
                      fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                    }}
                  >
                    {type}
                  </span>
                ))}
              </div>
            </td>
            <td data-seq-private="" className="var-amount-cell" style={{ textAlign: "right", fontWeight: 500 }}>
              {formatCurrency(group.totalAmount)}
            </td>
            <td data-seq-private="" className="var-amount-cell" style={{ textAlign: "right", fontWeight: 500, color: group.totalApproved > 0 ? "var(--success-color)" : "var(--text-muted)" }}>
              {formatCurrency(group.totalApproved)}
            </td>
            <td data-seq-private="" className="var-amount-cell" style={{ textAlign: "right", fontWeight: 500, color: group.totalWaiting > 0 ? "var(--warning-color)" : "var(--success-color)" }}>
              {formatCurrency(group.totalWaiting)}
            </td>
            <td style={{ textAlign: "center" }}>
              <span
                style={{
                  background: (group.status || "").toLowerCase() === "valid" ? "#d1fae5" : (group.status || "").toLowerCase() === "invalid" ? "#fee2e2" : "#ffedd5",
                  color: (group.status || "").toLowerCase() === "valid" ? "#065f46" : (group.status || "").toLowerCase() === "invalid" ? "#991b1b" : "#9a3412",
                  border: `1px solid ${(group.status || "").toLowerCase() === "valid" ? "#6ee7b7" : (group.status || "").toLowerCase() === "invalid" ? "#fca5a5" : "#fdba74"}`,
                  padding: "4px 12px", borderRadius: "20px", fontSize: "0.7rem", fontWeight: 700,
                  textTransform: "capitalize", display: "inline-block", minWidth: "80px",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                }}
              >
                {(group.status || "Pending").charAt(0).toUpperCase() + (group.status || "Pending").slice(1).toLowerCase()}
              </span>
            </td>
            <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
              {(() => {
                const unreadCountsSafe = unreadCounts || {};
                const unread = group.items.reduce((sum, item) => sum + (unreadCountsSafe[item.id] || 0), 0) || (unreadCountsSafe[group.id] || 0);
                return (
                  <EntityCommentsBadge
                    flag={group.audit_flag}
                    unreadCount={unread}
                    onOpen={() => setCommentsTarget({
                      entityType: "variation",
                      entityId: group.items[0]?.id || group.id,
                      label: group.docket_no,
                    })}
                  />
                );
              })()}
            </td>
            <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
              <select
                className={`recon-select recon-select--${getReconciliationOption(group.reconciliation_status ?? null).key}`}
                value={group.reconciliation_status || ''}
                onChange={(e) => {
                  const val = e.target.value === '' ? null : e.target.value as ReconciliationStatus;
                  handleReconciliationChange(group, val);
                }}
                title={`Reconciliation: ${getReconciliationOption(group.reconciliation_status ?? null).label}`}
              >
                {RECONCILIATION_OPTIONS.map((opt) => (
                  <option key={opt.value ?? 'null'} value={opt.value || ''}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </td>
          </tr>
        );

        // --- Shared table renderer ---
        const renderTable = (
          rows: GroupedVariation[],
          totals: { total: number; approved: number; waiting: number; count: number },
          highlight?: 'awaiting' | 'paid',
        ) => (
          <div className="table-container" style={{ border: "none" }}>
            <table className="table">
              <thead>
                {totals.count > 0 && (
                  <tr style={{ backgroundColor: "var(--bg-secondary)", borderBottom: "2px solid var(--border-color)" }}>
                    <th colSpan={bulkMode ? 5 : 4} style={{ textAlign: "left", color: "var(--text-primary)" }}>Totals</th>
                    <th data-seq-private="" style={{ textAlign: "right", color: "var(--text-primary)" }}>{formatCurrency(totals.total)}</th>
                    <th data-seq-private="" style={{ textAlign: "right", color: "var(--text-primary)" }}>{formatCurrency(totals.approved)}</th>
                    <th data-seq-private="" style={{ textAlign: "right", color: "var(--text-primary)" }}>{formatCurrency(totals.waiting)}</th>
                    <th colSpan={3}></th>
                  </tr>
                )}
                <tr>
                  {bulkMode && (
                    <th style={{ width: '36px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={rows.length > 0 && rows.every(r => bulkSelected.has(r.id))}
                        onChange={(e) => {
                          const next = new Set(bulkSelected);
                          if (e.target.checked) {
                            rows.forEach(r => next.add(r.id));
                          } else {
                            rows.forEach(r => next.delete(r.id));
                          }
                          setBulkSelected(next);
                        }}
                        className="recon-checkbox"
                      />
                    </th>
                  )}
                  <th>Date</th>
                  <th style={{ textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em" }}>Docket #</th>
                  <th style={{ textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em", maxWidth: "200px" }}>Description</th>
                  <th style={{ textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em" }}>TYPE</th>
                  <th style={{ textAlign: "right" }}>Total Variance</th>
                  <th style={{ textAlign: "right" }}>Approved</th>
                  <th style={{ textAlign: "right" }}>Waiting</th>
                  <th style={{ textAlign: "center" }}>Status</th>
                  <th style={{ textAlign: "center", width: "60px" }}>Audit</th>
                  <th style={{ textAlign: "center", width: "110px", textTransform: "uppercase", fontSize: "0.75rem", letterSpacing: "0.05em" }}>Reconciliation</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => renderRow(row, idx, highlight))}
              </tbody>
            </table>
          </div>
        );

        // --- Render based on view mode ---
        if (viewMode === 'status') {
          return (
            <div key={`status-${viewMode}`} className="view-switch-animate">
              {/* Awaiting Variations */}
              <div className="card" style={{ marginBottom: "var(--spacing-xl)" }}>
                <div
                  style={{
                    padding: "10px 16px",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: "var(--warning-color)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    borderBottom: "1px solid var(--border-color)",
                    background: "linear-gradient(135deg, rgba(245,158,11,0.04), rgba(245,158,11,0.01))",
                  }}
                >
                  <CircleDashed size={16} />
                  Awaiting Variations
                  <span style={{
                    background: "var(--bg-tertiary)", color: "var(--text-secondary)",
                    padding: "2px 10px", borderRadius: "12px", fontSize: "0.78rem", fontWeight: 700,
                  }}>
                    {filteredTotals.awaiting.count}
                  </span>
                </div>
                {awaitingVariations.length === 0 ? (
                  <div style={{ padding: "24px", textAlign: "center", color: "var(--text-muted)", fontSize: "0.9rem" }}>
                    No awaiting variations. All variations are fully paid.
                  </div>
                ) : (
                  renderTable(awaitingVariations, filteredTotals.awaiting, 'awaiting')
                )}
              </div>

              {/* Paid Variations */}
              <div className="card">
                <div
                  style={{
                    padding: "10px 16px",
                    fontSize: "0.9rem",
                    fontWeight: 600,
                    color: "var(--success-color)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    borderBottom: "1px solid var(--border-color)",
                    background: "linear-gradient(135deg, rgba(34,197,94,0.04), rgba(34,197,94,0.01))",
                  }}
                >
                  <CheckCircle2 size={16} />
                  Paid Variations
                  <span style={{
                    background: "var(--bg-tertiary)", color: "var(--text-secondary)",
                    padding: "2px 10px", borderRadius: "12px", fontSize: "0.78rem", fontWeight: 700,
                  }}>
                    {filteredTotals.paid.count}
                  </span>
                </div>
                {paidVariations.length === 0 ? (
                  <div style={{ padding: "24px", textAlign: "center", color: "var(--text-muted)", fontSize: "0.9rem" }}>
                    No paid variations yet.
                  </div>
                ) : (
                  renderTable(paidVariations, filteredTotals.paid, 'paid')
                )}
              </div>
            </div>
          );
        }

        // Non-status modes: single sorted table
        return (
          <div key={`sorted-${viewMode}`} className="card view-switch-animate">
            <div
              style={{
                padding: "8px 16px", fontSize: "0.85em", color: "var(--text-muted)",
                display: "flex", alignItems: "center", gap: "6px",
                borderBottom: "1px solid var(--border-color)",
              }}
            >
              <Info size={14} />
              {viewMode === 'docket' && 'Sorted by docket number (ascending).'}
              {viewMode === 'date' && 'Sorted by variation date (newest first).'}
              {viewMode === 'variance' && 'Sorted by total variance (largest first).'}
              {' '}Click a row to view details.
            </div>
            {renderTable(sortedVariations, filteredTotals)}
          </div>
        );
      })()}

      </>
      )}


      {selectedGroup && (
        <VariationDetailModal
          groupedVariation={selectedGroup}
          project={project}
          onClose={() => setSelectedGroup(null)}
          onApprovalChange={handleApprovalChange}
        />
      )}

      {commentsTarget && (
        <EntityCommentsModal
          isOpen={true}
          onClose={() => {
            if (commentsTarget.entityType === "variation") {
              onMarkRead?.(commentsTarget.entityId);
            } else {
              onMarkHireRead?.(commentsTarget.entityId);
            }
            setCommentsTarget(null);
          }}
          entityType={commentsTarget.entityType}
          entityId={commentsTarget.entityId}
          entityLabel={commentsTarget.label}
          onFlagChange={(entityId, flag) => {
            if (commentsTarget.entityType === "hire_item") {
              // Update the local hire flag so the dot moves immediately
              onHireFlagChange?.(entityId, flag);
              return;
            }
            // Update local variation data so flag dot updates immediately
            const updated = variations.map((v) =>
              v.id === entityId ? { ...v, audit_flag: flag } : v,
            );
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
            onVariationsUpdate(updated as any);
          }}
        />
      )}

      {/* Preview Modal for Bulk Import */}
      <Modal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="1200px"
        title="Review Import"
        onConfirm={handleBulkImport}
        confirmText={importing ? "Importing..." : "Confirm Import"}
        confirmDisabled={
          importing ||
          validationResult?.valid.length === 0 ||
          (validationResult?.summary.errorCount || 0) > 0
        }
      >
        <div style={{ width: "100%" }}>
          {/* Progress Bar */}
          {importing && importProgress.total > 0 && (
            <div style={{ marginBottom: "2rem" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginBottom: "0.5rem",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                <span>Importing Variations...</span>
                <span>
                  {importProgress.processed} / {importProgress.total}
                </span>
              </div>
              <div
                style={{
                  width: "100%",
                  height: "8px",
                  background: "var(--bg-tertiary)",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${(importProgress.processed / importProgress.total) * 100}%`,
                    height: "100%",
                    background: "var(--primary-color)",
                    transition: "width 0.3s ease",
                  }}
                />
              </div>
            </div>
          )}
          {validationResult && (
            <div>
              {/* Summary Dashboard - Card Layout */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: "1.5rem",
                  marginBottom: "2rem",
                }}
              >
                {/* Total Variations Card */}
                <div
                  className="card"
                  style={{
                    padding: "1.5rem",
                    textAlign: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    border: "1px solid var(--border-color)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Total Variations
                  </div>
                  <div
                    style={{
                      fontSize: "2rem",
                      fontWeight: 700,
                      color: "var(--text-color)",
                    }}
                  >
                    {validationResult.summary.totalVariations}
                  </div>
                </div>

                {/* Status Card */}
                <div
                  className="card"
                  style={{
                    padding: "1.5rem",
                    textAlign: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    border: "1px solid var(--border-color)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Status
                  </div>
                  <div
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color:
                        validationResult.summary.errorCount > 0
                          ? "var(--danger)"
                          : "var(--success-color)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "0.5rem",
                    }}
                  >
                    {validationResult.summary.errorCount > 0 ? (
                      <>
                        <AlertTriangle size={24} />
                        {validationResult.summary.errorCount} Errors
                      </>
                    ) : (
                      <>
                        <CheckCircle2 size={24} /> Valid
                      </>
                    )}
                  </div>
                </div>

                {/* Approved Amount Card */}
                <div
                  className="card"
                  style={{
                    padding: "1.5rem",
                    textAlign: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    border: "1px solid var(--border-color)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Total Approved
                  </div>
                  <div data-seq-private=""
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "var(--text-primary)",
                    }}
                  >
                    {formatCurrency(validationResult.summary.totalApproved)}
                  </div>
                </div>

                {/* Waiting Amount Card */}
                <div
                  className="card"
                  style={{
                    padding: "1.5rem",
                    textAlign: "center",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    border: "1px solid var(--border-color)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      marginBottom: "0.5rem",
                    }}
                  >
                    Total Waiting
                  </div>
                  <div data-seq-private=""
                    style={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "var(--color-warning)",
                    }}
                  >
                    {formatCurrency(validationResult.summary.totalWaiting)}
                  </div>
                </div>
              </div>

              {/* Validation Messages */}
              <div style={{ marginBottom: "2rem" }}>
                {validationResult.warnings.filter((w: string) => {
                  // Extract row number to check current status
                  const match = w.match(/^Row (\d+):/);
                  if (match) {
                    const rowNum = parseInt(match[1], 10);
                    const index = rowNum - 2; // header + 0-index adjustment
                    // Only show warning if status is still 'duplicate'
                    // If user changed to 'valid', hide the warning
                    if (
                      validationResult.valid[index] &&
                      validationResult.valid[index].status === "valid"
                    ) {
                      return false;
                    }
                  }
                  return true;
                }).length > 0 && (
                    <div
                      className="alert alert-warning"
                      style={{ marginBottom: "1rem" }}
                    >
                      <AlertTriangle size={18} />
                      <div style={{ flex: 1 }}>
                        {validationResult.warnings
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                          .filter((w: any) => {
                            const match = w.match(/^Row (\d+):/);
                            if (match) {
                              const rowNum = parseInt(match[1], 10);
                              const index = rowNum - 2;
                              if (
                                validationResult.valid[index] &&
                                validationResult.valid[index].status === "valid"
                              ) {
                                return false;
                              }
                            }
                            return true;
                          })
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                          .map((w: any, i: number) => (
                            <div key={i}>{w}</div>
                          ))}
                        {/* Ignore All Duplicates Button */}
                        {validationResult.valid.filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                          (v: any) => v.status === "duplicate",
                        ).length > 0 && (
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{
                                marginTop: "0.5rem",
                                background: "#d97706",
                                color: "white",
                                border: "none",
                              }}
                              onClick={() => {
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                                setValidationResult((prev: any) => {
                                  if (!prev) return prev;
                                  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                                  const newValid = prev.valid.map((v: any) =>
                                    v.status === "duplicate"
                                      ? { ...v, status: "valid" as const }
                                      : v,
                                  );
                                  return { ...prev, valid: newValid };
                                });
                                addToast({
                                  title: "Duplicates Ignored",
                                  message:
                                    "All duplicates marked as valid for import",
                                  type: "success",
                                });
                              }}
                            >
                              Ignore All Duplicates (
                              {
                                validationResult.valid.filter(
                                  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- auto-suppressed for safety
                                  (v: any) => v.status === "duplicate",
                                ).length
                              }
                              )
                            </button>
                          )}
                      </div>
                    </div>
                  )}

                {validationResult.errors.length > 0 && (
                  <div
                    className="alert alert-danger"
                    style={{ marginBottom: "1rem" }}
                  >
                    <AlertTriangle size={18} />
                    <div style={{ flex: 1 }}>
                      {validationResult.errors.map((e: { row: number; field: string; message: string }, i: number) => (
                        <div key={i}>
                          <strong>Row {e.row}:</strong> {e.field} - {e.message}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Import Results Summary */}
                {(importResults.successCount > 0 ||
                  importResults.failCount > 0) && (
                    <div
                      className={`alert ${importResults.failCount > 0
                        ? "alert-warning"
                        : "alert-success"
                        }`}
                      style={{
                        marginBottom: "1rem",
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                      }}
                    >
                      <div style={{ flex: 1 }}>
                        <strong>Import Results:</strong>{" "}
                        {importResults.successCount} succeeded,{" "}
                        {importResults.failCount} failed
                        {importResults.errors.length > 0 && (
                          <div
                            style={{
                              fontSize: "0.85rem",
                              marginTop: "4px",
                              color: "var(--danger)",
                            }}
                          >
                            {importResults.errors.slice(0, 3).map((err, i) => (
                              <div key={i}>{err}</div>
                            ))}
                            {importResults.errors.length > 3 && (
                              <div>
                                ...and {importResults.errors.length - 3} more
                                errors
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {importResults.failCount > 0 && !importing && (
                        <button
                          className="btn btn-warning btn-sm"
                          onClick={handleRetryFailed}
                        >
                          Retry Failed ({importResults.failCount})
                        </button>
                      )}
                      {importResults.successCount > 0 && !importing && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => window.location.reload()}
                        >
                          Refresh to See Data
                        </button>
                      )}
                    </div>
                  )}
              </div>

              {/* Data Table Preview */}
              {validationResult.valid.length > 0 && (
                <div style={{ marginBottom: "0rem" }}>
                  <div className="flex align-center justify-between mb-4">
                    <h3
                      style={{
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        marginBottom: "0",
                      }}
                    >
                      Items to Import
                    </h3>
                    <div
                      className="flex align-center gap-2"
                      style={{
                        background: "#f1f5f9",
                        padding: "4px 8px",
                        borderRadius: "6px",
                      }}
                    >
                      <span style={{ fontSize: "0.85em", fontWeight: 500 }}>
                        Date Format:
                      </span>
                      <select
                        value={dateFormat}
                        onChange={(e) =>
                          setDateFormat(e.target.value as "DMY" | "MDY")
                        }
                        style={{
                          padding: "2px 6px",
                          borderRadius: "4px",
                          border: "1px solid #cbd5e1",
                          fontSize: "0.85em",
                        }}
                      >
                        <option value="DMY">AU (DD/MM)</option>
                        <option value="MDY">US (MM/DD)</option>
                      </select>
                    </div>
                  </div>
                  <div
                    className="table-container"
                    style={{ maxHeight: "400px", overflowY: "auto" }}
                  >
                    <table className="table" style={{ width: "100%" }}>
                      <thead
                        style={{
                          background: "var(--bg-secondary)",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          borderBottom: "1px solid var(--border-color)",
                        }}
                      >
                        {/* Group Header Row */}
                        <tr
                          style={{
                            background: "#f8fafc",
                            textTransform: "uppercase",
                            fontSize: "0.75rem",
                            letterSpacing: "0.05em",
                            color: "var(--text-muted)",
                          }}
                        >
                          <th
                            colSpan={5}
                            style={{
                              textAlign: "center",
                              borderRight: "1px solid var(--border-color)",
                            }}
                          >
                            Variation Details
                          </th>
                          <th
                            colSpan={3}
                            style={{
                              textAlign: "center",
                              background: "#f0fdf4",
                              color: "#166534",
                              borderRight: "1px solid var(--border-color)",
                            }}
                          >
                            Day Labour (Hours)
                          </th>
                          <th
                            colSpan={2}
                            style={{
                              textAlign: "center",
                              background: "#fffbeb",
                              color: "#92400e",
                              borderRight: "1px solid var(--border-color)",
                            }}
                          >
                            Sat Contract
                          </th>
                          <th
                            colSpan={4}
                            style={{
                              textAlign: "center",
                              background: "#eff6ff",
                              color: "#1e40af",
                              borderRight: "1px solid var(--border-color)",
                            }}
                          >
                            Materials/Others
                          </th>
                          <th
                            colSpan={3}
                            style={{
                              textAlign: "center",
                              background: "#fff7ed",
                              color: "#9a3412",
                            }}
                          >
                            Financials
                          </th>
                          <th
                            style={{ background: "var(--bg-secondary)" }}
                          ></th>
                        </tr>
                        {/* Column Header Row */}
                        <tr>
                          {/* Details */}
                          <th style={{ minWidth: "50px", padding: "8px 12px" }}>
                            Status
                          </th>
                          <th
                            style={{ minWidth: "140px", padding: "8px 12px" }}
                          >
                            Date
                          </th>
                          <th
                            style={{ minWidth: "150px", padding: "8px 12px" }}
                          >
                            Title
                          </th>
                          <th
                            style={{ minWidth: "100px", padding: "8px 12px" }}
                          >
                            Type
                          </th>
                          <th
                            style={{
                              minWidth: "100px",
                              padding: "8px 12px",
                              borderRight: "1px solid var(--border-color)",
                            }}
                          >
                            Status
                          </th>

                          {/* Day Labour */}
                          <th
                            style={{
                              textAlign: "right",
                              minWidth: "70px",
                              padding: "8px 12px",
                              background: "#f0fdf4",
                            }}
                          >
                            Normal
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              minWidth: "70px",
                              padding: "8px 12px",
                              background: "#f0fdf4",
                            }}
                          >
                            OT 1.5x
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              minWidth: "70px",
                              padding: "8px 12px",
                              background: "#f0fdf4",
                              borderRight: "1px solid var(--border-color)",
                            }}
                          >
                            Sat-NC
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              minWidth: "70px",
                              padding: "8px 12px",
                              background: "#fffbeb",
                              borderRight: "1px solid var(--border-color)",
                            }}
                          >
                            Sat(C)OT
                          </th>

                          {/* Materials */}
                          <th
                            style={{
                              minWidth: "180px",
                              padding: "8px 12px",
                              background: "#eff6ff",
                            }}
                          >
                            Item Name
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              minWidth: "60px",
                              padding: "8px 12px",
                              background: "#eff6ff",
                            }}
                          >
                            Qty
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              minWidth: "80px",
                              padding: "8px 12px",
                              background: "#eff6ff",
                            }}
                          >
                            Cost
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              minWidth: "90px",
                              padding: "8px 12px",
                              background: "#eff6ff",
                              borderRight: "1px solid var(--border-color)",
                            }}
                          >
                            Total
                          </th>

                          {/* Financials */}
                          <th
                            style={{
                              textAlign: "right",
                              minWidth: "100px",
                              padding: "8px 12px",
                              background: "#fff7ed",
                            }}
                          >
                            Approved
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              minWidth: "100px",
                              padding: "8px 12px",
                              background: "#fff7ed",
                            }}
                          >
                            Waiting
                          </th>
                          <th
                            style={{
                              textAlign: "right",
                              minWidth: "100px",
                              padding: "8px 12px",
                              background: "#fff7ed",
                            }}
                          >
                            Total Val
                          </th>

                          {/* Notes */}
                          <th
                            style={{ minWidth: "200px", padding: "8px 12px" }}
                          >
                            Notes
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {validationResult.valid.map((v: Record<string, string>, i: number) => (
                          <tr
                            key={i}
                            style={{
                              backgroundColor:
                                importStatus[i] === "success"
                                  ? "transparent" // User requested no highlight for success
                                  : importStatus[i] === "failed"
                                    ? "rgba(239, 68, 68, 0.05)" // Very subtle red
                                    : importStatus[i] === "importing"
                                      ? "rgba(59, 130, 246, 0.05)" // Very subtle blue
                                      : "transparent",
                              transition: "background-color 0.3s ease",
                            }}
                          >
                            <td style={{ verticalAlign: "middle" }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  height: "100%",
                                }}
                              >
                                {(!importStatus[i] ||
                                  importStatus[i] === "pending") && (
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        padding: "2px 8px",
                                        borderRadius: "12px",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        background: "#f3f4f6", // gray-100
                                        color: "#6b7280", // gray-500
                                        border: "1px solid #e5e7eb",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <CircleDashed size={10} /> Pending
                                    </span>
                                  )}
                                {importStatus[i] === "importing" && (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      padding: "2px 8px",
                                      borderRadius: "12px",
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      background: "#eff6ff", // blue-50
                                      color: "#3b82f6", // blue-500
                                      border: "1px solid #bfdbfe",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <Loader2
                                      size={10}
                                      className="animate-spin"
                                    />{" "}
                                    Importing
                                  </span>
                                )}
                                {importStatus[i] === "success" && (
                                  <span
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px",
                                      padding: "2px 8px",
                                      borderRadius: "12px",
                                      fontSize: "11px",
                                      fontWeight: 600,
                                      background: "#ecfdf5", // green-50
                                      color: "#10b981", // green-500
                                      border: "1px solid #a7f3d0",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <CheckCircle2 size={10} /> Success
                                  </span>
                                )}
                                {importStatus[i] === "failed" && (
                                  <div
                                    title={importRowErrors[i] || "Failed"}
                                    style={{ cursor: "help" }}
                                  >
                                    <span
                                      style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        padding: "2px 8px",
                                        borderRadius: "12px",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        background: "#fef2f2", // red-50
                                        color: "#ef4444", // red-500
                                        border: "1px solid #fecaca",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      <XCircle size={10} /> Failed
                                    </span>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              <input
                                type="date"
                                value={v.date}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "date",
                                    e.target.value,
                                  )
                                }
                                style={{
                                  padding: "4px",
                                  width: "130px",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "inherit",
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "white";
                                  e.target.style.borderColor =
                                    "var(--primary-color)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "transparent";
                                }}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={v.title || ""}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "title",
                                    e.target.value,
                                  )
                                }
                                placeholder="Title"
                                style={{
                                  padding: "4px",
                                  width: "120px",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "inherit",
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "white";
                                  e.target.style.borderColor =
                                    "var(--primary-color)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "transparent";
                                }}
                              />
                            </td>
                            <td>
                              <select
                                value={v.debit_credit || "variance"}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "debit_credit",
                                    e.target.value,
                                  )
                                }
                                style={{
                                  padding: "4px",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "inherit",
                                }}
                              >
                                <option value="variance">Variance</option>
                                <option value="credit">Credit</option>
                              </select>
                            </td>
                            {/* Status dropdown */}
                            <td>
                              <select
                                value={v.status || "pending"}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "status",
                                    e.target.value,
                                  )
                                }
                                style={{
                                  padding: "4px",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "0.8em",
                                  color:
                                    v.status === "valid"
                                      ? "var(--color-success)"
                                      : v.status === "invalid"
                                        ? "var(--color-error)"
                                        : v.status === "duplicate"
                                          ? "#d97706"
                                          : "var(--text-muted)",
                                }}
                              >
                                <option value="pending">Pending</option>
                                <option value="valid">Valid</option>
                                <option value="duplicate">Duplicate</option>
                                <option value="invalid">Invalid</option>
                              </select>
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <input data-seq-private
                                type="number"
                                value={v.hours_lvl_1}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "hours_lvl_1",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                style={{
                                  padding: "4px",
                                  width: "80px",
                                  textAlign: "right",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "inherit",
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "white";
                                  e.target.style.borderColor =
                                    "var(--primary-color)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "transparent";
                                }}
                                min={0}
                                step={0.5}
                              />
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <input data-seq-private
                                type="number"
                                value={v.hours_lvl_2}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "hours_lvl_2",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                style={{
                                  padding: "4px",
                                  width: "80px",
                                  textAlign: "right",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "inherit",
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "white";
                                  e.target.style.borderColor =
                                    "var(--primary-color)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "transparent";
                                }}
                                min={0}
                                step={0.5}
                              />
                            </td>
                            {/* DL Saturday */}
                            <td style={{ textAlign: "right" }}>
                              <input data-seq-private
                                type="number"
                                value={v.saturday_work_qty}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "saturday_work_qty",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                style={{
                                  padding: "4px",
                                  width: "60px",
                                  textAlign: "right",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "inherit",
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "white";
                                  e.target.style.borderColor =
                                    "var(--primary-color)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "transparent";
                                }}
                                min={0}
                                step={0.5}
                              />
                            </td>
                            {/* Sat Contract OT */}
                            <td style={{ textAlign: "right" }}>
                              <input data-seq-private
                                type="number"
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                value={(v as any).sat_contract_ot_qty || 0}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "sat_contract_ot_qty",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                style={{
                                  padding: "4px",
                                  width: "60px",
                                  textAlign: "right",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "inherit",
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "white";
                                  e.target.style.borderColor =
                                    "var(--primary-color)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "transparent";
                                }}
                                min={0}
                                step={0.5}
                              />
                            </td>
                            {/* Material Title */}
                            <td>
                              <input
                                type="text"
                                value={v.material_title || ""}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "material_title",
                                    e.target.value,
                                  )
                                }
                                placeholder="Material/Others..."
                                style={{
                                  padding: "4px",
                                  width: "90px",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "0.85em",
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "white";
                                  e.target.style.borderColor =
                                    "var(--primary-color)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "transparent";
                                }}
                              />
                            </td>
                            {/* Material Qty */}
                            <td style={{ textAlign: "right" }}>
                              <input data-seq-private
                                type="number"
                                value={v.material_qty || ""}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "material_qty",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                placeholder="1"
                                style={{
                                  padding: "4px",
                                  width: "50px",
                                  textAlign: "right",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "inherit",
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "white";
                                  e.target.style.borderColor =
                                    "var(--primary-color)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "transparent";
                                }}
                                min={0}
                                step={1}
                              />
                            </td>
                            {/* Material Cost (unit) */}
                            <td style={{ textAlign: "right" }}>
                              <input data-seq-private
                                type="number"
                                value={v.material_cost}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "material_cost",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                style={{
                                  padding: "4px",
                                  width: "70px",
                                  textAlign: "right",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "inherit",
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "white";
                                  e.target.style.borderColor =
                                    "var(--primary-color)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "transparent";
                                }}
                                min={0}
                                step={0.01}
                              />
                            </td>
                            {/* Mat Total (calculated: Qty × Cost) */}
                            <td data-seq-private=""
                              style={{
                                textAlign: "right",
                                color: "var(--text-secondary)",
                              }}
                            >
                              {formatCurrency(
                                (Number(v.material_qty) || 1) * (Number(v.material_cost) || 0),
                              )}
                            </td>
                            <td style={{ textAlign: "right" }}>
                              <input data-seq-private
                                type="number"
                                value={v.approved_amount}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "approved_amount",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                style={{
                                  padding: "4px",
                                  width: "100px",
                                  textAlign: "right",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "inherit",
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "white";
                                  e.target.style.borderColor =
                                    "var(--primary-color)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "transparent";
                                }}
                                min={0}
                                step={0.01}
                              />
                            </td>
                            <td data-seq-private=""
                              style={{
                                textAlign: "right",
                                color:
                                  Number(v.waiting_amount) > 0
                                    ? "var(--color-warning)"
                                    : "var(--text-muted)",
                              }}
                            >
                              {formatCurrency(Number(v.waiting_amount) || 0)}
                            </td>
                            {/* Total - Moved to end */}
                            <td data-seq-private="" style={{ textAlign: "right", fontWeight: 600 }}>
                              {formatCurrency(Number(v.total) || 0)}
                            </td>
                            <td>
                              <input
                                type="text"
                                value={v.notes}
                                onChange={(e) =>
                                  handleVariationChange(
                                    i,
                                    "notes",
                                    e.target.value,
                                  )
                                }
                                style={{
                                  padding: "4px",
                                  width: "100%",
                                  border: "1px solid transparent",
                                  background: "transparent",
                                  fontFamily: "inherit",
                                  fontSize: "inherit",
                                }}
                                onFocus={(e) => {
                                  e.target.style.background = "white";
                                  e.target.style.borderColor =
                                    "var(--primary-color)";
                                }}
                                onBlur={(e) => {
                                  e.target.style.background = "transparent";
                                  e.target.style.borderColor = "transparent";
                                }}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Progress Claim Modal */}

      {/* Onboarding Tour */}
      {tour.activeTour && (
        <TourEngine
          tour={tour.activeTour}
          onComplete={tour.complete}
          onSkip={tour.skip}
        />
      )}
    </div>
  );
}
